/* ==========================================================================
   Longevity Pet — Quiz personalizzato · MOTORE
   --------------------------------------------------------------------------
   Tutto lato browser, nessun backend. Legge la configurazione (asset JSON),
   applica gli override dell'editor tema, renderizza il flusso domande,
   costruisce le raccomandazioni (Fase 3 §5), legge i prodotti live da Shopify
   (/products/{handle}.js), calcola dose/ciclo/quantita (§7) e invia a Klaviyo
   dopo consenso (§8).

   Contenuti/logica: _quiz-docs/longevity-pet-quiz-fase1.md
   Tecnica:          _quiz-docs/longevity-pet-quiz-fase3-specifica-tecnica.md

   Nessuna dipendenza esterna. Isolato: non tocca lo stato globale del tema.
   ========================================================================== */
(function () {
  'use strict';

  /* ----------------------------------------------------------------------
     Utility di base
     ---------------------------------------------------------------------- */
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === 'class') node.className = attrs[k];
        else if (k === 'html') node.innerHTML = attrs[k];
        else if (k === 'text') node.textContent = attrs[k];
        else if (k.slice(0, 2) === 'on' && typeof attrs[k] === 'function') {
          node.addEventListener(k.slice(2), attrs[k]);
        } else if (attrs[k] != null && attrs[k] !== false) {
          node.setAttribute(k, attrs[k]);
        }
      });
    }
    (children || []).forEach(function (c) {
      if (c == null) return;
      node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return node;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Prima lettera maiuscola (per nome del pet e inizio frase).
  function capitalize(s) {
    if (!s) return s;
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  // Sostituisce {nome} nei testi con il nome del pet (o "il tuo amico").
  function fill(text, name) {
    if (!text) return '';
    return String(text).replace(/\{nome\}/g, name || 'il tuo amico');
  }

  function deepMerge(base, over) {
    if (over == null) return base;
    if (typeof base !== 'object' || Array.isArray(base)) return over;
    var out = Object.assign({}, base);
    Object.keys(over).forEach(function (k) {
      if (over[k] === '' || over[k] == null) return; // vuoto = usa la config
      if (typeof over[k] === 'object' && !Array.isArray(over[k])) {
        out[k] = deepMerge(base[k] || {}, over[k]);
      } else {
        out[k] = over[k];
      }
    });
    return out;
  }

  // R25: formatter monetario di Shopify (money.js canonico), usato per aggiornare
  // il totale del carrello in testata con Shopify.money_format. `cents` = prezzo in
  // centesimi (come da /cart.js total_price). Nessuna dipendenza esterna.
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '{{amount}}';
    function withDelimiters(number, precision, thousands, decimal) {
      precision = (precision == null) ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents2 = parts[1] ? (decimal + parts[1]) : '';
      return dollars + cents2;
    }
    var value = '';
    var match = formatString.match(placeholderRegex);
    switch (match ? match[1] : 'amount') {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  /* ----------------------------------------------------------------------
     Eta e fasce (Fase 1 §2)
     ---------------------------------------------------------------------- */
  function computeAgeYears(birthdate) {
    if (!birthdate || birthdate.unknown || !birthdate.year) return null;
    var day = birthdate.day || 1;
    var birth = new Date(birthdate.year, (birthdate.month || 1) - 1, day);
    var now = new Date();
    var years = now.getFullYear() - birth.getFullYear();
    var m = now.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) years--;
    return years;
  }

  // Etichetta interna: cucciolo / teenager / adulto / senior
  function ageBandFromYears(species, years) {
    if (years == null) return null;
    if (species === 'gatto') {
      if (years < 1) return 'cucciolo';
      if (years < 3) return 'teenager';
      if (years < 10) return 'adulto';
      return 'senior';
    }
    if (years < 1) return 'cucciolo';
    if (years < 3) return 'teenager';
    if (years < 7) return 'adulto';
    return 'senior';
  }

  function isSenior(profile) {
    if (profile.ageYears != null) {
      return profile.species === 'gatto' ? profile.ageYears >= 10 : profile.ageYears >= 7;
    }
    // fallback: fascia scelta manualmente
    var seniorBands = { cane: '7+', gatto: '10+' };
    return profile.ageBandValue === seniorBands[profile.species];
  }

  // Peso >= "media" (10 kg in su) per cane
  function weightIsMediumUp(band) {
    return band === '10-25' || band === '25-40' || band === '>40';
  }
  function weightIsLargeUp(band) {
    return band === '25-40' || band === '>40';
  }

  // R17.2: un consiglio è "di prevenzione" (riempitivo) quando NON risponde a una
  // problematica scelta dall'utente — cioè la regola senior, il percorso
  // prevenzione o i prodotti "di supporto". Le problematiche selezionate
  // (concern / combo / cura, regime existing/asNeeded) NON sono prevenzione e
  // hanno sempre priorità: non vanno mai escluse a favore della prevenzione.
  function isPreventionItem(item) {
    return !!(item && (item.support || item.source === 'senior' ||
      item.source === 'prevenzione' || item.regime === 'preventive'));
  }

  // R21: costruisce l'item "combo" (kit multi-prodotto) da una voce comboBundles.
  // Usato sia dalla formazione bundle-first (applyBundleFirst, problematiche) sia
  // dall'iniezione in prevenzione (buildCandidates, Kit Movimento & Digestione).
  // Porta anche la quantità consigliata (R21 PARTE 1) e la variante per fascia di
  // peso (Kit Orecchie, R21 PARTE 1.3).
  function buildComboItem(combo, handle, regime, source) {
    return {
      slug: '__combo__' + handle, kind: 'bundle', regime: regime, source: source,
      comboHandle: handle, comboDesc: combo.descriptionBySpecies,
      comboComponents: combo.componentsBySpecies, comboMatches: combo.matches,
      comboComponentCycles: combo.componentCycles || null,
      comboComponentFormats: combo.componentFormats || null,
      comboRecommendedQuantity: combo.recommendedQuantity || null,
      comboVariantByWeight: combo.variantByWeight || null,
      comboCoverageDaysByBand: combo.coverageDaysByBand || null
    };
  }

  // GA4: stesso meccanismo degli eventi quiz_landing_ (lp-quiz-landing.js),
  // gtag diretto se definito, nessuna logica di consenso propria. Il try/catch
  // garantisce che un errore di tracking non fermi mai il quiz né Klaviyo.
  function gaTrack(name, params) {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, params || {});
    } catch (e) { /* il tracking non blocca il quiz */ }
  }

  /* ----------------------------------------------------------------------
     Quiz: costruttore
     ---------------------------------------------------------------------- */
  function Quiz(root, config, rootUrl, configUrl) {
    this.root = root;
    this.config = config;
    this.rootUrl = (rootUrl || '/').replace(/\/$/, '');
    // Cartella asset del tema, derivata dall'URL della config (stessa cartella).
    // Serve a costruire gli URL delle icone via JS (equivalente di asset_url).
    this.assetBase = (configUrl || '').split('?')[0].replace(/[^/]*$/, '');
    this.mount = root.querySelector('.lp-quiz__mount') || root;
    this.answers = { concerns: [], cura: [] };
    this.currentToken = null;
    this.questionsById = {};
    (config.questions || []).forEach(function (q) { this.questionsById[q.id] = q; }, this);

    // Mappa problematica → icona (dalle opzioni della domanda concerns)
    this.concernIcons = {};
    var concernsQ = this.questionsById.concerns;
    if (concernsQ) {
      (concernsQ.options || []).forEach(function (o) { if (o.icon) this.concernIcons[o.value] = o.icon; }, this);
    }
    // R22 B.3: la voce "cura" non è più tra le opzioni concerns (R21), quindi la
    // sua icona non veniva più mappata → l'etichetta "Cura e igiene quotidiana"
    // (key 'cura') restava senza icona. Ripristiniamo il mapping su icon-cura.png.
    if (!this.concernIcons.cura) this.concernIcons.cura = 'icon-cura.png';
  }

  Quiz.prototype.q = function (id) { return this.questionsById[id]; };
  Quiz.prototype.petName = function () { return capitalize(this.answers.name || ''); };

  // <img> icona con fallback: se il file non esiste, l'elemento si rimuove da solo
  // (niente immagini rotte). Ritorna null se non c'è un nome file.
  Quiz.prototype.iconEl = function (filename, cls) {
    if (!filename) return null;
    var img = el('img', {
      class: cls || 'lp-quiz__icon', src: this.assetBase + filename, alt: '', 'aria-hidden': 'true', loading: 'lazy'
    });
    img.addEventListener('error', function () { if (img.parentNode) img.parentNode.removeChild(img); });
    return img;
  };

  // R18 (3.2): trova l'icona dell'AREA/problematica sostenuta da un prodotto,
  // invertendo la mappa config.concerns (concern → products) e riusando le icone
  // già associate alle problematiche. Es. artopet_difesa → articolazioni,
  // probioticpet → intestino, hairskinpet → pelo, oralpropet → igiene_orale.
  Quiz.prototype.areaIconForSlug = function (slug) {
    var concerns = this.config.concerns || {};
    var keys = Object.keys(concerns);
    for (var i = 0; i < keys.length; i++) {
      if (((concerns[keys[i]].products) || []).indexOf(slug) !== -1) {
        var icon = this.concernIcons[keys[i]];
        if (icon) return icon;
      }
    }
    return null;
  };

  // Icona dell'area che ha generato un consiglio (per i blocchi risultati)
  Quiz.prototype.iconForItem = function (item) {
    if (item.source === 'combo') {
      var m = (item.comboMatches && item.comboMatches[0]) || null;
      return (m && this.concernIcons[m]) || 'icon-prevenzione.png';
    }
    // R18 (3.2): prevenzione/supporto mostrano SEMPRE l'icona dell'area sostenuta
    // dal prodotto (mai più lo scudo con cuore icon-prevenzione). Fallback allo
    // scudo solo se il prodotto non è mappato a nessuna problematica.
    if (item.support || item.source === 'senior' || item.source === 'prevenzione' || item.regime === 'preventive') {
      return this.areaIconForSlug(item.slug) || 'icon-prevenzione.png';
    }
    var src = item.source || '';
    if (src.indexOf('cura_') === 0) src = 'cura';
    return this.concernIcons[src] || 'icon-prevenzione.png';
  };

  // Lista ordinata dei passi. R21 PARTE 3: sequenza FISSA (nessun passo
  // condizionale) → il contatore "Passo X di N" ha un N fisso e uguale per cane e
  // gatto. 3.1: la fascia d'età non è più un passo a sé (compare inline nella
  // pagina anagrafica). 3.2: l'igiene quotidiana ('cura') è ora un passo
  // OBBLIGATORIO sempre presente (non più un followup condizionale). N = 11
  // domande (esclusi intro e results).
  Quiz.prototype.activeSteps = function () {
    return ['species', 'name', 'anagrafica', 'breed', 'weight_band', 'concerns',
      'cura', 'diet', 'supplement_history', 'source', 'email', 'results'];
  };

  Quiz.prototype.start = function () {
    var self = this;
    // Invio = "Avanti" in TUTTE le domande valide (incluse scelta singola e
    // multipla). Ascolta su document: dopo un clic su un'opzione il focus torna
    // al body (fuori dal contenitore), quindi un listener sul solo root non
    // riceverebbe l'evento. preventDefault blocca l'attivazione del bottone
    // focalizzato (così Invio avanza invece di ri-cliccare). La textarea è
    // esclusa; la tendina razze ferma la propagazione se è aperta.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      if (self.currentToken === 'results' || !self.currentToken) return;
      if (!self.root.isConnected) return; // quiz non più in pagina
      // Sulla schermata iniziale (hero) Invio avvia il quiz
      if (self.currentToken === 'intro') { e.preventDefault(); self.beginQuiz(); return; }
      var tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'textarea') return;
      if (self.stepValid()) { e.preventDefault(); self.goNext(); }
    });
    this.currentToken = 'intro';
    this.renderStep();
  };

  // Avvia il quiz vero e proprio dalla schermata iniziale (hero)
  Quiz.prototype.beginQuiz = function () {
    this.root.classList.remove('lp-quiz--intro');
    this.currentToken = 'species';
    this.renderStep();
    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  Quiz.prototype.goNext = function () {
    var steps = this.activeSteps();
    var i = steps.indexOf(this.currentToken);
    if (i < steps.length - 1) {
      this.currentToken = steps[i + 1];
      this.renderStep();
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  Quiz.prototype.goBack = function () {
    var steps = this.activeSteps();
    var i = steps.indexOf(this.currentToken);
    if (i > 0) {
      this.currentToken = steps[i - 1];
      this.renderStep();
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  /* ----------------------------------------------------------------------
     Rendering del passo corrente
     ---------------------------------------------------------------------- */
  Quiz.prototype.renderStep = function () {
    if (this.currentToken === 'intro') return this.renderIntro();
    if (this.currentToken === 'results') return this.renderResults();
    this.root.classList.remove('lp-quiz--intro');

    var steps = this.activeSteps();
    var idx = steps.indexOf(this.currentToken);
    var total = steps.length - 1; // esclude "results" → N domande (11)
    var progress = Math.round((idx / total) * 100);

    // GA4 quiz_start: una volta per percorso, all'ingresso nella prima domanda.
    // Copre "Inizia il test", Invio sull'intro e "Ripeti il test" (che salta
    // l'intro). Il flag si azzera in restart(); "Indietro" non lo rifà partire.
    if (idx === 0 && !this._gaStarted) {
      this._gaStarted = true;
      gaTrack('quiz_start');
    }

    var wrap = el('div', { class: 'lp-quiz__step' });
    var pageHeader = this.renderPageHeader();
    if (pageHeader) wrap.appendChild(pageHeader);
    wrap.appendChild(this.renderHeader());
    // R21 PARTE 3.3 / R22 A.1: contatore "Domanda X di N". N è fisso (activeSteps
    // non ha più passi condizionali) → non "salta" ed è uguale per specie.
    wrap.appendChild(el('p', { class: 'lp-quiz__stepcount', text: 'Domanda ' + (idx + 1) + ' di ' + total }));
    wrap.appendChild(el('div', { class: 'lp-quiz__progress' }, [
      el('div', { class: 'lp-quiz__progress-bar', style: 'width:' + progress + '%' })
    ]));

    var body;
    switch (this.currentToken) {
      case 'species': body = this.renderSingle(this.q('species')); break;
      case 'name': body = this.renderText(this.q('name')); break;
      case 'anagrafica': body = this.renderAnagrafica(); break;
      case 'breed': body = this.renderBreed(this.q('breed')); break;
      case 'weight_band': body = this.renderSingleBySpecies(this.q('weight_band'), 'weight_band'); break;
      case 'concerns': body = this.renderMulti(this.q('concerns'), 'concerns'); break;
      case 'cura': body = this.renderCura(); break;
      case 'diet': body = this.renderSingle(this.q('diet')); break;
      case 'supplement_history': body = this.renderSingle(this.q('supplement_history')); break;
      case 'source': body = this.renderSingle(this.q('source')); break;
      case 'email': body = this.renderEmail(this.q('email')); break;
    }
    wrap.appendChild(body);
    wrap.appendChild(this.renderNav(idx));

    this.mount.innerHTML = '';
    this.mount.appendChild(wrap);
  };

  Quiz.prototype.renderHeader = function () {
    var header = el('div', { class: 'lp-quiz__header' });
    var logo = (this.config.settings.assets || {}).logoUrl;
    if (logo) header.appendChild(el('img', { class: 'lp-quiz__logo', src: logo, alt: 'Longevity Pet' }));
    return header;
  };

  // Intestazione di pagina compatta (R10.3): "Il meglio per il tuo migliore
  // amico? …". Testi in settings.pageHeader. Mostrata su domande e risultati,
  // MAI sulla schermata iniziale (là c'è già la hero, sarebbe ridondante).
  // Ritorna null se non c'è testo, così non occupa spazio.
  Quiz.prototype.renderPageHeader = function () {
    var ph = (this.config.settings || {}).pageHeader || {};
    if (!ph.title && !ph.subtitle) return null;
    var box = el('div', { class: 'lp-quiz__pageheader' });
    if (ph.title) box.appendChild(el('p', { class: 'lp-quiz__pageheader-title', text: ph.title }));
    if (ph.subtitle) box.appendChild(el('p', { class: 'lp-quiz__pageheader-sub', text: ph.subtitle }));
    return box;
  };

  // Risolve un asset: URL assoluto/root → invariato; altrimenti nome file =
  // asset del tema (stessa cartella della config, equivalente di asset_url).
  Quiz.prototype.resolveAsset = function (name) {
    if (!name) return '';
    if (/^(https?:)?\/\//.test(name) || name.charAt(0) === '/') return name;
    return this.assetBase + name;
  };

  // Schermata iniziale come hero (R9.6): testo + immagine verticale.
  // Contenuti in settings.intro; immagine in settings.assets.heroImageUrl.
  Quiz.prototype.renderIntro = function () {
    var self = this;
    var settings = this.config.settings || {};
    var intro = settings.intro || {};
    var assets = settings.assets || {};
    this.root.classList.add('lp-quiz--intro');

    var content = el('div', { class: 'lp-quiz__intro-content' });
    content.appendChild(this.renderHeader());
    if (intro.duration) {
      content.appendChild(el('span', { class: 'lp-quiz__intro-duration', text: intro.duration }));
    }
    if (intro.title) {
      content.appendChild(el('h1', { class: 'lp-quiz__intro-title', text: intro.title }));
    }
    if (intro.subtitle) {
      content.appendChild(el('p', { class: 'lp-quiz__intro-subtitle', text: intro.subtitle }));
    }
    var reassur = intro.reassurances || [];
    if (reassur.length) {
      var ul = el('ul', { class: 'lp-quiz__intro-reassurances' });
      reassur.forEach(function (r) {
        ul.appendChild(el('li', { class: 'lp-quiz__intro-reassurance' }, [
          el('span', { class: 'lp-quiz__intro-check', 'aria-hidden': 'true' }),
          el('span', { text: r })
        ]));
      });
      content.appendChild(ul);
    }
    content.appendChild(el('button', {
      type: 'button', class: 'lp-quiz__btn lp-quiz__btn--primary lp-quiz__intro-cta',
      text: intro.cta || 'Inizia il test',
      onclick: function () { self.beginQuiz(); }
    }));
    if (intro.trust) {
      content.appendChild(el('p', { class: 'lp-quiz__intro-trust', text: intro.trust }));
    }

    var inner = el('div', { class: 'lp-quiz__intro-inner' }, [content]);

    // Immagine verticale: affiancata su desktop, sopra su mobile (via CSS).
    var heroSrc = this.resolveAsset(assets.heroImageUrl);
    if (heroSrc) {
      var media = el('div', { class: 'lp-quiz__intro-media' });
      var img = el('img', { class: 'lp-quiz__intro-img', src: heroSrc, alt: '', loading: 'eager' });
      // Se il file non esiste, rimuovi la colonna immagine (nessuna foto rotta)
      img.addEventListener('error', function () {
        if (media.parentNode) media.parentNode.removeChild(media);
        inner.classList.add('lp-quiz__intro-inner--noimg');
      });
      media.appendChild(img);
      inner.appendChild(media);
    } else {
      inner.classList.add('lp-quiz__intro-inner--noimg');
    }

    this.mount.innerHTML = '';
    this.mount.appendChild(el('div', { class: 'lp-quiz__intro' }, [inner]));
  };

  Quiz.prototype.renderPrompt = function (q) {
    return el('h2', { class: 'lp-quiz__prompt', text: fill(q.prompt, this.petName()) });
  };

  // Aggiorna solo la classe is-selected di un gruppo di opzioni (niente re-render).
  // buttons = [{ el, value }]; selectedValue = valore attualmente scelto.
  function syncSingle(buttons, selectedValue) {
    buttons.forEach(function (b) { b.el.classList.toggle('is-selected', b.value === selectedValue); });
  }

  // Dropdown personalizzato riutilizzabile (R10.1): stesso markup/classi e
  // comportamento della tendina razze (.lp-quiz__combo), ma per liste chiuse di
  // opzioni {value,label}. Usato per Mese e Anno, così casella e tendina aperta
  // sono IDENTICHE a quelle delle razze in tutti gli stati (niente sfondo nativo
  // "rosa", niente <select> del browser). Nessuna ricerca a testo: l'input è di
  // sola lettura e funge da selettore. Ritorna { wrap, setDisabled }.
  // opts: { options:[{value,label}], value, placeholder, ariaLabel, onChoose(value) }
  function buildSelectCombo(opts) {
    var options = opts.options || [];
    var selectedValue = opts.value == null ? '' : opts.value;

    function labelFor(v) {
      for (var i = 0; i < options.length; i++) {
        if (String(options[i].value) === String(v)) return options[i].label;
      }
      return '';
    }

    var wrap = el('div', { class: 'lp-quiz__combo' });
    var input = el('input', {
      type: 'text', class: 'lp-quiz__input lp-quiz__combo-input',
      value: labelFor(selectedValue), placeholder: opts.placeholder || '',
      'aria-label': opts.ariaLabel || '', autocomplete: 'off',
      role: 'combobox', readonly: 'readonly'
    });
    var panel = el('div', { class: 'lp-quiz__combo-panel', role: 'listbox' });
    wrap.appendChild(input);
    wrap.appendChild(panel);

    var activeIndex = -1;

    function buildOptions() {
      panel.innerHTML = '';
      options.forEach(function (o, i) {
        var cls = 'lp-quiz__combo-option';
        if (i === activeIndex) cls += ' is-active';
        if (String(selectedValue) === String(o.value)) cls += ' is-selected';
        var opt = el('div', {
          class: cls, role: 'option',
          onmousedown: function (e) { e.preventDefault(); choose(o); }
        });
        opt.textContent = o.label;
        panel.appendChild(opt);
      });
    }
    function open() {
      if (input.disabled) return;
      // parti dall'opzione già selezionata per la navigazione con le frecce
      if (activeIndex < 0) {
        for (var i = 0; i < options.length; i++) {
          if (String(options[i].value) === String(selectedValue)) { activeIndex = i; break; }
        }
      }
      panel.classList.add('is-open');
      buildOptions();
      scrollActive();
    }
    function close() { panel.classList.remove('is-open'); activeIndex = -1; }
    function choose(o) {
      selectedValue = o.value;
      input.value = o.label;
      if (opts.onChoose) opts.onChoose(o.value);
      close();
    }
    function scrollActive() { var a = panel.querySelector('.is-active'); if (a && a.scrollIntoView) a.scrollIntoView({ block: 'nearest' }); }

    input.addEventListener('focus', open);
    input.addEventListener('click', open); // touch/iOS: apre anche se il focus non scatta
    input.addEventListener('blur', function () { setTimeout(close, 120); });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!panel.classList.contains('is-open')) { open(); return; }
        activeIndex = Math.min(activeIndex + 1, options.length - 1);
        buildOptions(); scrollActive();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (!panel.classList.contains('is-open')) { open(); return; }
        activeIndex = Math.max(activeIndex - 1, 0);
        buildOptions(); scrollActive();
      } else if (e.key === 'Enter') {
        // Con tendina aperta e opzione evidenziata, Invio la seleziona e ferma
        // la propagazione (così il quiz non avanza nello stesso Invio).
        if (panel.classList.contains('is-open') && activeIndex >= 0 && options[activeIndex]) {
          e.preventDefault(); e.stopPropagation();
          choose(options[activeIndex]);
        }
      } else if (e.key === 'Escape') {
        close();
      }
    });

    return {
      wrap: wrap,
      setDisabled: function (d) { input.disabled = !!d; if (d) close(); }
    };
  }

  // Scelta singola con opzioni piatte (options)
  Quiz.prototype.renderSingle = function (q) {
    var self = this;
    var frag = el('div', {});
    frag.appendChild(this.renderPrompt(q));
    // R22 B.1: "Che pet hai?" → tessere affiancate con icona grande sopra la
    // parola (modificatore CSS lp-quiz__options--species, solo per questa domanda).
    var optsClass = 'lp-quiz__options' + (q.id === 'species' ? ' lp-quiz__options--species' : '');
    var opts = el('div', { class: optsClass });
    var buttons = [];
    (q.options || []).forEach(function (o) {
      var btn = self.optionButton(o.label, self.answers[q.id] === o.value, function () {
        self.answers[q.id] = o.value;
        // aggiorna SOLO le classi delle opzioni (fix R7: niente sfarfallio)
        syncSingle(buttons, o.value);
        self.refreshNav();
      }, null, o.icon);
      buttons.push({ el: btn, value: o.value });
      opts.appendChild(btn);
    });
    frag.appendChild(opts);
    return frag;
  };

  // Scelta singola con optionsBySpecies (age_band, weight_band)
  Quiz.prototype.renderSingleBySpecies = function (q, storeKey) {
    var self = this;
    var frag = el('div', {});
    frag.appendChild(this.renderPrompt(q));
    var list = (q.optionsBySpecies && q.optionsBySpecies[this.answers.species]) || [];
    var opts = el('div', { class: 'lp-quiz__options' });
    var buttons = [];
    list.forEach(function (o) {
      var btn = self.optionButton(o.label, self.answers[storeKey] === o.value, function () {
        self.answers[storeKey] = o.value;
        syncSingle(buttons, o.value);
        self.refreshNav();
      });
      buttons.push({ el: btn, value: o.value });
      opts.appendChild(btn);
    });
    frag.appendChild(opts);
    return frag;
  };

  Quiz.prototype.optionButton = function (label, selected, onClick, line2, icon) {
    var body = line2
      ? el('span', { class: 'lp-quiz__option-body' }, [
          el('span', { class: 'lp-quiz__option-line1', text: label }),
          el('span', { class: 'lp-quiz__option-line2', text: line2 })
        ])
      : el('span', { class: 'lp-quiz__option-body' }, [el('span', { text: label })]);
    // Icona opzionale, stesso meccanismo/fallback delle problematiche (R11)
    var children = [];
    var ic = this.iconEl(icon, 'lp-quiz__option-icon');
    if (ic) children.push(ic);
    children.push(body);
    return el('button', {
      type: 'button',
      class: 'lp-quiz__option' + (selected ? ' is-selected' : ''),
      onclick: onClick
    }, children);
  };

  Quiz.prototype.renderText = function (q) {
    var self = this;
    var frag = el('div', {});
    frag.appendChild(this.renderPrompt(q));
    var input = el('input', {
      type: 'text', class: 'lp-quiz__input', value: this.answers.name || '',
      placeholder: 'Es. Simba', 'aria-label': 'Nome',
      oninput: function (e) { self.answers.name = e.target.value.trim(); self.refreshNav(); }
    });
    frag.appendChild(el('label', { class: 'lp-quiz__field' }, [input]));
    return frag;
  };

  // Schermata anagrafica unica: data di nascita + sesso + sterilizzazione
  // (le due domande "dataOnly" sono facoltative e non bloccano l'avanzamento).
  Quiz.prototype.renderAnagrafica = function () {
    var self = this;
    var q = this.q('birthdate');
    var name = this.petName();
    var frag = el('div', {});
    frag.appendChild(el('h2', { class: 'lp-quiz__prompt', text: 'I dati di ' + (name || 'lui') }));

    var bd = this.answers.birthdate || (this.answers.birthdate = {});
    var isUnknown = !!bd.unknown;

    var birthBlock = el('div', { class: 'lp-quiz__ana-field' });
    birthBlock.appendChild(el('span', { class: 'lp-quiz__field-label', text: fill(q.prompt, name) }));

    // R8.1: entrambi i blocchi (campi data / nota fasce d'età) restano SEMPRE nel
    // DOM e si alternano con la classe .is-hidden. Il toggle "Non lo so" cambia
    // solo classi, stato disabilitato e testo del pulsante: niente renderStep(),
    // quindi nessuno sfarfallio (coerente col fix R7 delle altre opzioni).
    // R10.1: Mese e Anno usano lo stesso dropdown personalizzato della razza
    // (buildSelectCombo) — casella e tendina identiche alle razze, niente
    // <select> nativo né sfondo "rosa".
    var months = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
    var monthCombo = buildSelectCombo({
      options: months.map(function (m, i) { return { value: i + 1, label: m }; }),
      value: bd.month || '', placeholder: 'Mese', ariaLabel: 'Mese',
      onChoose: function (v) { self.setBirth('month', v); }
    });

    var years = [];
    var y = new Date().getFullYear();
    for (var yr = y; yr >= y - 25; yr--) years.push(yr);
    var yearCombo = buildSelectCombo({
      options: years.map(function (yy) { return { value: yy, label: String(yy) }; }),
      value: bd.year || '', placeholder: 'Anno', ariaLabel: 'Anno',
      onChoose: function (v) { self.setBirth('year', v); }
    });

    var dayInput = el('input', {
      type: 'number', min: 1, max: 31, class: 'lp-quiz__input',
      placeholder: 'Giorno (facoltativo)', value: bd.day || '', 'aria-label': 'Giorno',
      oninput: function (e) { self.setBirth('day', e.target.value); }
    });

    // Blocco campi data (Mese/Anno + Giorno + helper)
    var dateBlock = el('div', { class: 'lp-quiz__birth-fields' + (isUnknown ? ' is-hidden' : '') }, [
      el('div', { class: 'lp-quiz__row' }, [monthCombo.wrap, yearCombo.wrap]),
      el('label', { class: 'lp-quiz__field', style: 'margin:0.6rem 0 0' }, [dayInput])
    ]);
    var helper = q.helper ? fill(q.helper, name) : '';
    if (helper) dateBlock.appendChild(el('p', { class: 'lp-quiz__hint lp-quiz__hint--field', text: helper }));

    // R21 PARTE 3.1: la domanda sulla fascia d'età compare INLINE, nella stessa
    // pagina, sotto il toggle "Non lo so". Visibile solo quando "Non lo so" è
    // attivo; se si reinserisce una data (toggle off) sparisce. Nessuna pagina
    // aggiuntiva. Il valore resta in answers.age_band (usato solo se la data è
    // sconosciuta; buildProfile dà comunque priorità alla data se presente).
    var ageBandQ = this.q('age_band');
    var ageList = (ageBandQ && ageBandQ.optionsBySpecies && ageBandQ.optionsBySpecies[this.answers.species]) || [];
    var ageBlock = el('div', { class: 'lp-quiz__ana-field lp-quiz__age-band' + (isUnknown ? '' : ' is-hidden') });
    if (ageBandQ) ageBlock.appendChild(el('span', { class: 'lp-quiz__field-label', text: fill(ageBandQ.prompt, name) }));
    var ageOpts = el('div', { class: 'lp-quiz__options' });
    var ageButtons = [];
    ageList.forEach(function (o) {
      var abtn = self.optionButton(o.label, self.answers.age_band === o.value, function () {
        self.answers.age_band = o.value;
        syncSingle(ageButtons, o.value); // fix R7: solo classi, niente re-render
        self.refreshNav();
      });
      ageButtons.push({ el: abtn, value: o.value });
      ageOpts.appendChild(abtn);
    });
    ageBlock.appendChild(ageOpts);

    birthBlock.appendChild(dateBlock);

    // Disabilita i campi data quando "Non lo so" è attivo (e li riattiva altrimenti)
    function setDateDisabled(disabled) {
      monthCombo.setDisabled(disabled);
      yearCombo.setDisabled(disabled);
      dayInput.disabled = disabled;
    }
    setDateDisabled(isUnknown);

    // Toggle "Non lo so" ben visibile (fix v4 #11) — solo toggle di classe/stato (R8.1)
    if (q.unknownButton) {
      var unknownBtn = el('button', {
        type: 'button',
        class: 'lp-quiz__unknown-toggle' + (isUnknown ? ' is-selected' : ''),
        text: (isUnknown ? '✓ ' : '') + (q.unknownLabel || 'Non lo so'),
        'aria-pressed': isUnknown ? 'true' : 'false',
        onclick: function () {
          var nowUnknown = !self.answers.birthdate.unknown;
          if (nowUnknown) { self.answers.birthdate.unknown = true; }   // usa le fasce d'età
          else { delete self.answers.birthdate.unknown; }              // torna all'inserimento data
          dateBlock.classList.toggle('is-hidden', nowUnknown);
          ageBlock.classList.toggle('is-hidden', !nowUnknown);         // 3.1: fascia d'età inline
          setDateDisabled(nowUnknown);
          unknownBtn.classList.toggle('is-selected', nowUnknown);
          unknownBtn.setAttribute('aria-pressed', nowUnknown ? 'true' : 'false');
          unknownBtn.textContent = (nowUnknown ? '✓ ' : '') + (q.unknownLabel || 'Non lo so');
          self.refreshNav();
        }
      });
      birthBlock.appendChild(unknownBtn);
    }
    birthBlock.appendChild(ageBlock);
    frag.appendChild(birthBlock);

    // Domande facoltative (sesso, sterilizzazione)
    frag.appendChild(this.renderOptionalSingle(this.q('sex'), 'sex'));
    frag.appendChild(this.renderOptionalSingle(this.q('neutered'), 'neutered'));
    return frag;
  };

  // Scelta singola facoltativa in linea (dataOnly): cliccabile e deselezionabile
  Quiz.prototype.renderOptionalSingle = function (q, storeKey) {
    var self = this;
    if (!q) return el('div', {});
    var group = el('div', { class: 'lp-quiz__ana-field' });
    group.appendChild(el('span', { class: 'lp-quiz__field-label', text: fill(q.prompt, this.petName()) + ' (facoltativo)' }));
    var opts = el('div', { class: 'lp-quiz__options lp-quiz__options--inline' });
    var buttons = [];
    (q.options || []).forEach(function (o) {
      var btn = self.optionButton(o.label, self.answers[storeKey] === o.value, function () {
        // toggle: ricliccando la stessa opzione si deseleziona
        self.answers[storeKey] = (self.answers[storeKey] === o.value) ? null : o.value;
        // aggiorna SOLO le classi (fix R7)
        syncSingle(buttons, self.answers[storeKey]);
        self.refreshNav();
      });
      buttons.push({ el: btn, value: o.value });
      opts.appendChild(btn);
    });
    group.appendChild(opts);
    return group;
  };

  Quiz.prototype.setBirth = function (part, value) {
    var bd = this.answers.birthdate || {};
    if (bd.unknown) bd = {};
    bd[part] = value ? parseInt(value, 10) : null;
    this.answers.birthdate = bd;
    this.refreshNav();
  };

  // Tendina razze personalizzata (stile brand): ricerca + pannello a comparsa.
  // "Meticcio"/"Non lo so" in cima, poi le razze in ordine alfabetico.
  Quiz.prototype.renderBreed = function (q) {
    var self = this;
    var frag = el('div', {});
    frag.appendChild(this.renderPrompt(q));

    var list = (q.breedList && q.breedList[this.answers.species]) || [];
    // Extra per specie (cane: Meticcio/Non lo so; gatto: Europeo/Non lo so)
    var extraDefs = (q.extraOptionsBySpecies && q.extraOptionsBySpecies[this.answers.species]) || q.extraOptions || [];
    var extra = extraDefs.map(function (o) { return o.label; });
    var ordered = (q.extraOptionsPosition === 'top') ? extra.concat(list) : list.concat(extra);

    var wrap = el('div', { class: 'lp-quiz__combo' });
    var input = el('input', {
      type: 'text', class: 'lp-quiz__input lp-quiz__combo-input',
      value: this.answers.breed || '', placeholder: 'Scegli o cerca la razza',
      'aria-label': 'Razza', autocomplete: 'off', role: 'combobox'
    });
    var panel = el('div', { class: 'lp-quiz__combo-panel', role: 'listbox' });
    wrap.appendChild(input);
    wrap.appendChild(panel);
    frag.appendChild(wrap);

    var activeIndex = -1;
    var filtered = ordered.slice();

    function buildOptions() {
      var term = input.value.trim().toLowerCase();
      filtered = ordered.filter(function (n) { return !term || n.toLowerCase().indexOf(term) !== -1; });
      panel.innerHTML = '';
      if (!filtered.length) {
        panel.appendChild(el('div', { class: 'lp-quiz__combo-empty', text: 'Nessuna razza trovata' }));
        return;
      }
      filtered.forEach(function (n, i) {
        var cls = 'lp-quiz__combo-option';
        if (i === activeIndex) cls += ' is-active';
        if (self.answers.breed === n) cls += ' is-selected';
        var opt = el('div', {
          class: cls, role: 'option',
          onmousedown: function (e) { e.preventDefault(); choose(n); }
        });
        opt.textContent = n;
        panel.appendChild(opt);
      });
    }
    function open() { panel.classList.add('is-open'); buildOptions(); }
    function close() { panel.classList.remove('is-open'); activeIndex = -1; }
    function choose(n) { self.answers.breed = n; input.value = n; self.refreshNav(); close(); }
    function scrollActive() { var a = panel.querySelector('.is-active'); if (a && a.scrollIntoView) a.scrollIntoView({ block: 'nearest' }); }
    // R28: etichetta canonica se il testo digitato corrisponde ESATTAMENTE a una
    // voce dell'elenco; altrimenti null (testo parziale → nessun commit).
    function matchOption(text) {
      var t = (text || '').trim().toLowerCase();
      if (!t) return null;
      for (var i = 0; i < ordered.length; i++) {
        if (ordered[i].toLowerCase() === t) return ordered[i];
      }
      return null;
    }

    input.addEventListener('focus', open);
    input.addEventListener('input', function () {
      // R28: mai salvare testo grezzo/parziale. La razza si committa solo scegliendo
      // una voce (choose) o digitandone l'etichetta esatta. Testo parziale → null.
      self.answers.breed = matchOption(input.value) || null;
      activeIndex = -1;
      self.refreshNav();
      open();
    });
    input.addEventListener('blur', function () {
      setTimeout(function () {
        close();
        // R28: se una razza è stata scelta, il campo mostra la sua etichetta intera.
        if (self.answers.breed) input.value = self.answers.breed;
      }, 120);
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!panel.classList.contains('is-open')) open();
        activeIndex = Math.min(activeIndex + 1, filtered.length - 1);
        buildOptions(); scrollActive();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = Math.max(activeIndex - 1, 0);
        buildOptions(); scrollActive();
      } else if (e.key === 'Enter') {
        // Se la tendina è aperta con un'opzione evidenziata, Invio la seleziona
        // (e ferma la propagazione così il quiz non avanza nello stesso Invio).
        if (panel.classList.contains('is-open') && activeIndex >= 0 && filtered[activeIndex]) {
          e.preventDefault(); e.stopPropagation();
          choose(filtered[activeIndex]);
        }
      } else if (e.key === 'Escape') {
        close();
      }
    });

    return frag;
  };

  // Scelta multipla a due righe (concerns). Il click aggiorna SOLO le classi
  // delle opzioni (fix R7): niente re-render dell'intero step, niente sfarfallio.
  Quiz.prototype.renderMulti = function (q, storeKey) {
    var self = this;
    var frag = el('div', {});
    frag.appendChild(this.renderPrompt(q));
    if (q.twoLine) frag.appendChild(el('p', { class: 'lp-quiz__hint', text: 'Puoi scegliere più opzioni.' }));
    var opts = el('div', { class: 'lp-quiz__options' });
    var buttons = [];
    // Risincronizza tutte le opzioni dalle risposte (serve per la mutua
    // esclusione tra "prevenzione" e le altre problematiche).
    function syncAll() {
      var arr = self.answers[storeKey] || [];
      buttons.forEach(function (b) { b.el.classList.toggle('is-selected', arr.indexOf(b.value) !== -1); });
      self.refreshNav();
    }
    (q.options || []).forEach(function (o) {
      var btn = self.multiOption(o, storeKey, syncAll);
      buttons.push({ el: btn, value: o.value });
      opts.appendChild(btn);
    });
    frag.appendChild(opts);
    return frag;
  };

  Quiz.prototype.multiOption = function (o, storeKey, syncAll) {
    var self = this;
    var arr = this.answers[storeKey] || (this.answers[storeKey] = []);
    var selected = arr.indexOf(o.value) !== -1;
    var btn = el('button', {
      type: 'button',
      class: 'lp-quiz__option' + (selected ? ' is-selected' : ''),
      onclick: function () {
        var a = self.answers[storeKey];
        var i = a.indexOf(o.value);
        if (i === -1) a.push(o.value); else a.splice(i, 1);
        // "prevenzione" e le altre problematiche si escludono a vicenda
        if (o.value === 'prevenzione' && i === -1) {
          self.answers[storeKey] = ['prevenzione'];
        } else if (o.value !== 'prevenzione' && i === -1) {
          self.answers[storeKey] = a.filter(function (v) { return v !== 'prevenzione'; });
        }
        if (syncAll) syncAll();
      }
    }, [
      this.iconEl(o.icon, 'lp-quiz__option-icon'),
      el('span', { class: 'lp-quiz__option-body' }, [
        el('span', { class: 'lp-quiz__option-line1', text: o.line1 || o.label }),
        o.line2 ? el('span', { class: 'lp-quiz__option-line2', text: o.line2 }) : null
      ]),
      // Il segno di spunta è gestito via CSS in base a .is-selected (fix R7)
      el('span', { class: 'lp-quiz__option-check' })
    ]);
    return btn;
  };

  // R21 PARTE 3.2: igiene quotidiana — domanda OBBLIGATORIA e multi-select con
  // opzione esclusiva "No, nessuna in particolare" (q.exclusiveOption). Selezionare
  // "No" deseleziona le altre e soddisfa l'obbligo; scegliere una sotto-voce
  // deseleziona il "No". Stessa meccanica R7 (solo classi) e mutua esclusione come
  // "prevenzione" in renderMulti. Le risposte vanno in answers.cura.
  Quiz.prototype.renderCura = function () {
    var self = this;
    var q = this.q('cura');
    var exclusive = q.exclusiveOption;
    var frag = el('div', {});
    frag.appendChild(el('h2', { class: 'lp-quiz__prompt', text: fill(q.prompt, this.petName()) }));
    frag.appendChild(el('p', { class: 'lp-quiz__hint', text: 'Puoi scegliere più opzioni.' }));
    var list = (q.optionsBySpecies && q.optionsBySpecies[this.answers.species]) || [];
    var opts = el('div', { class: 'lp-quiz__options' });
    var buttons = [];
    function syncAll() {
      var arr = self.answers.cura || [];
      buttons.forEach(function (b) { b.el.classList.toggle('is-selected', arr.indexOf(b.value) !== -1); });
      self.refreshNav();
    }
    list.forEach(function (o) {
      var arr = self.answers.cura || (self.answers.cura = []);
      var btn = self.optionButton(o.label, arr.indexOf(o.value) !== -1, function () {
        var a = self.answers.cura;
        var i = a.indexOf(o.value);
        if (i === -1) a.push(o.value); else a.splice(i, 1);
        // "No, nessuna in particolare" è esclusiva rispetto alle sotto-voci
        if (o.value === exclusive && i === -1) {
          self.answers.cura = [exclusive];
        } else if (o.value !== exclusive && i === -1) {
          self.answers.cura = self.answers.cura.filter(function (v) { return v !== exclusive; });
        }
        syncAll();
      }, null, o.icon);
      buttons.push({ el: btn, value: o.value });
      opts.appendChild(btn);
    });
    frag.appendChild(opts);
    return frag;
  };

  Quiz.prototype.renderEmail = function (q) {
    var self = this;
    var frag = el('div', {});
    frag.appendChild(this.renderPrompt(q));
    // R22 A.4: sconto come sottotitolo piccolo, subito sotto la domanda
    if (q.subtitle) frag.appendChild(el('p', { class: 'lp-quiz__prompt-sub', text: fill(q.subtitle, this.petName()) }));
    var input = el('input', {
      type: 'email', class: 'lp-quiz__input', value: this.answers.email || '',
      placeholder: 'La tua email', 'aria-label': 'Email',
      oninput: function (e) { self.answers.email = e.target.value.trim(); self.refreshNav(); }
    });
    frag.appendChild(el('label', { class: 'lp-quiz__field' }, [input]));

    if (q.consentCheckbox) {
      var consent = el('label', { class: 'lp-quiz__consent' }, [
        el('input', {
          type: 'checkbox',
          onchange: function (e) { self.answers.consent = e.target.checked; self.refreshNav(); }
        }),
        el('span', {
          html: escapeHtml(q.consentLabel || '').replace('privacy policy',
            '<a href="' + escapeHtml(q.privacyPolicyUrl || '/policies/privacy-policy') + '" target="_blank" rel="noopener">privacy policy</a>')
        })
      ]);
      frag.appendChild(consent);
    }
    return frag;
  };

  /* ----------------------------------------------------------------------
     Navigazione e validazione
     ---------------------------------------------------------------------- */
  Quiz.prototype.stepValid = function () {
    var a = this.answers;
    switch (this.currentToken) {
      case 'species': return !!a.species;
      case 'name': return !!(a.name && a.name.length);
      case 'anagrafica':
        // R21 PARTE 3.1: con "Non lo so" la fascia d'età è inline e OBBLIGATORIA
        // (l'avanzamento richiede una fascia scelta); altrimenti serve Mese+Anno.
        if (a.birthdate && a.birthdate.unknown) return !!a.age_band;
        return !!(a.birthdate && a.birthdate.month && a.birthdate.year);
      case 'breed': return !!a.breed;
      case 'weight_band': return !!a.weight_band;
      case 'concerns': return (a.concerns || []).length > 0;
      case 'cura': return (a.cura || []).length > 0; // R21 PARTE 3.2: obbligatoria ("No" incluso)
      case 'diet': return !!a.diet;
      case 'supplement_history': return !!a.supplement_history;
      case 'source': return !!a.source;
      case 'email':
        var okEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email || '');
        return okEmail && a.consent === true;
      default: return true;
    }
  };

  // Il pulsante "Avanti" è sempre visibile in ogni passo (disabilitato se
  // il passo non è ancora valido). Nessun auto-avanzamento.
  Quiz.prototype.autoAdvances = function () {
    return false;
  };

  Quiz.prototype.renderNav = function (idx) {
    var self = this;
    var nav = el('div', { class: 'lp-quiz__nav' });
    if (idx > 0) {
      nav.appendChild(el('button', {
        type: 'button', class: 'lp-quiz__btn lp-quiz__btn--secondary', text: 'Indietro',
        onclick: function () { self.goBack(); }
      }));
    }
    nav.appendChild(el('div', { class: 'lp-quiz__nav-spacer' }));
    if (!this.autoAdvances()) {
      var isEmail = this.currentToken === 'email';
      var btn = el('button', {
        type: 'button', class: 'lp-quiz__btn lp-quiz__btn--primary',
        text: isEmail ? 'Scopri il piano' : 'Avanti',
        disabled: this.stepValid() ? false : 'disabled',
        onclick: function () { if (self.stepValid()) self.goNext(); }
      });
      this._nextBtn = btn;
      nav.appendChild(btn);
    } else {
      this._nextBtn = null;
    }
    return nav;
  };

  // Aggiorna solo lo stato del pulsante "Avanti" senza ridisegnare tutto
  Quiz.prototype.refreshNav = function () {
    if (this._nextBtn) {
      this._nextBtn.disabled = this.stepValid() ? false : true;
    }
  };

  /* ----------------------------------------------------------------------
     Profilo e motore di raccomandazione (Fase 3 §5)
     ---------------------------------------------------------------------- */
  Quiz.prototype.buildProfile = function () {
    var a = this.answers;
    var years = computeAgeYears(a.birthdate);
    var curaExclusive = (this.q('cura') || {}).exclusiveOption;
    return {
      species: a.species,
      name: capitalize(a.name),
      birthdate: a.birthdate,
      ageYears: years,
      ageBand: years != null ? ageBandFromYears(a.species, years) : (a.age_band ? bandLabel(a.age_band) : null),
      ageBandValue: a.age_band || null,
      breed: a.breed,
      weight: a.weight_band,
      concerns: (a.concerns || []).slice(),
      // R21 PARTE 3.2: "No, nessuna in particolare" (exclusiveOption) NON è una
      // voce di cura → escluso dal profilo, così non genera prodotti né Kit Cura.
      cura: (a.cura || []).filter(function (v) { return v !== curaExclusive; }),
      diet: a.diet,
      sex: a.sex || null,
      neutered: a.neutered || null,
      supplementHistory: a.supplement_history,
      source: a.source,
      email: a.email
    };
  };

  function bandLabel(v) {
    // mappa il valore fascia manuale a un'etichetta interna
    if (v === '7+' || v === '10+') return 'senior';
    if (v === '0-1') return 'cucciolo';
    if (v === '1-3') return 'teenager';
    return 'adulto';
  }

  // Slug problematiche che hanno una mappatura prodotto in config.concerns
  Quiz.prototype.effectiveConcerns = function (profile) {
    var map = this.config.concerns || {};
    var selected = profile.concerns.concat(profile.cura);
    return selected.filter(function (s) { return !!map[s]; });
  };

  // Costruisce la lista ordinata dei candidati (prima del filtro disponibilita)
  Quiz.prototype.buildCandidates = function (profile) {
    var cfg = this.config;
    var species = profile.species;
    var items = [];
    var seen = {};

    function add(slug, kind, regime, source) {
      if (seen[slug]) return;
      seen[slug] = true;
      items.push({ slug: slug, kind: kind, regime: regime, source: source });
    }

    var effective = this.effectiveConcerns(profile);
    var wantJointsKit = false; // R21 PARTE 2: prevenzione articolazioni → Kit Movimento & Digestione

    if (effective.length) {
      // Da problematiche selezionate (regime "existing")
      effective.forEach(function (concern) {
        var m = cfg.concerns[concern];
        (m.products || []).forEach(function (slug) {
          var kind = cfg.bundles[slug] ? 'bundle' : 'product';
          add(slug, kind, m.cycleRegime || 'existing', concern);
        });
      });
    } else {
      // Logica di prevenzione (regime "preventive")
      var prev = cfg.prevention || {};
      (prev.always || []).forEach(function (slug) { add(slug, 'product', 'preventive', 'prevenzione'); });
      (prev.oral || []).forEach(function (slug) { add(slug, 'product', 'preventive', 'prevenzione'); });
      // R21 PARTE 2: cane taglia grande/gigante (qualsiasi età) o senior taglia
      // media+ → in prevenzione si consiglia il Kit Movimento & Digestione (kit,
      // stesse quantità del problema) invece di ArtoPet singolo. Il kit viene
      // iniettato dopo il filtro per specie (i combo hanno slug __combo__* non in
      // cfg.bundles); il dedup componenti (applyBundleFirst) rimuove ProbioticPet
      // singolo di "always", evitando il doppione.
      if (species === 'cane' && (weightIsLargeUp(profile.weight) || (isSenior(profile) && weightIsMediumUp(profile.weight)))) {
        wantJointsKit = true;
      }
    }

    // Regola Senior cane: ArtoPet Difesa sempre se cane senior e peso >= media
    var sr = cfg.settings.seniorRule || {};
    if (species === 'cane' && isSenior(profile) && weightIsMediumUp(profile.weight)) {
      add(sr.product || 'artopet_difesa', 'product', 'preventive', 'senior');
    }

    // Filtro per specie
    items = items.filter(function (it) {
      if (it.kind === 'bundle') {
        var b = cfg.bundles[it.slug];
        return b && b.handleBySpecies && b.handleBySpecies[species];
      }
      var p = cfg.products[it.slug];
      return p && (!p.species || p.species.indexOf(species) !== -1);
    });

    // R21 PARTE 2: iniezione del Kit Movimento & Digestione in prevenzione.
    // Fatto DOPO il filtro per specie (il combo ha slug __combo__* che non è in
    // cfg.bundles e il filtro lo scarterebbe) e PRIMA di applyBundleFirst, così
    // il dedup componenti assorbe ProbioticPet (e l'eventuale ArtoPet della regola
    // senior) dentro il kit. Solo cane (handle combo cane-only).
    if (wantJointsKit) {
      var mdHandle = (cfg.prevention || {}).jointsKitCombo;
      var md = this.findComboByHandle(mdHandle, species);
      if (md) items.unshift(buildComboItem(md, mdHandle, 'preventive', 'prevenzione'));
    }

    // Sostituzione bundle-first
    items = this.applyBundleFirst(items, profile);

    // Ordinamento: (R17.2) prima le problematiche selezionate, poi la
    // prevenzione; a parità i bundle (kit/combo) prima; infine priorityOrder
    // come tie-break (usato soprattutto tra i prodotti di prevenzione).
    var priority = ((cfg.prevention || {}).priorityOrder) || [];
    items.sort(function (x, y) {
      var cx = isPreventionItem(x) ? 1 : 0;
      var cy = isPreventionItem(y) ? 1 : 0;
      if (cx !== cy) return cx - cy;
      var bx = x.kind === 'bundle' ? 0 : 1;
      var by = y.kind === 'bundle' ? 0 : 1;
      if (bx !== by) return bx - by;
      var px = priority.indexOf(x.slug); if (px === -1) px = 99;
      var py = priority.indexOf(y.slug); if (py === -1) py = 99;
      return px - py;
    });

    return items;
  };

  // Bundle-first: cura multipla -> Kit Cura; combo problematiche -> combo bundle
  Quiz.prototype.applyBundleFirst = function (items, profile) {
    var self = this;
    var cfg = this.config;
    if (cfg.settings.bundleFirst === false) return items;

    // 1) Piu sotto-voci "cura" -> Kit Cura e Benessere
    var curaSlugs = ['gocce_oculari', 'detergente_auricolare', 'crema_polpastrelli'];
    var curaCount = profile.cura.length;
    if (curaCount >= 2 && cfg.bundles.kit_cura && cfg.bundles.kit_cura.handleBySpecies[profile.species]) {
      items = items.filter(function (it) { return curaSlugs.indexOf(it.slug) === -1; });
      items.unshift({ slug: 'kit_cura', kind: 'bundle', regime: 'existing', source: 'cura' });
    }

    // 2) Combo bundle (Fase 3 §6 / R19): forma OGNI combo applicabile in modo
    //    INDIPENDENTE. Un combo si forma quando tutte le sue `matches` sono tra le
    //    problematiche selezionate; la formazione NON consuma né rimuove i
    //    componenti dalla lista. Così un componente condiviso (es. ProbioticPet,
    //    presente sia nel kit alito sia nel kit orecchie) NON impedisce la
    //    formazione di un secondo kit che lo contiene — decisione R19 opzione A:
    //    ProbioticPet può comparire in più kit (sono bundle distinti con SKU
    //    propri). L'unica sostituzione qui è di un BUNDLE singolo che rappresenta
    //    una problematica coperta dal combo (es. cute_prurito → kit_soluzione_prurito
    //    sostituito dalla combo prurito+stress): NON è il caso del componente
    //    condiviso, ma una vera sostituzione bundle→combo.
    var selected = profile.concerns.concat(profile.cura);
    var supersededBundles = {};
    (cfg.comboBundles || []).forEach(function (combo) {
      var handle = combo.handleBySpecies && combo.handleBySpecies[profile.species];
      if (!handle) return;
      var allMatch = (combo.matches || []).every(function (m) { return selected.indexOf(m) !== -1; });
      if (!allMatch) return;
      (combo.matches || []).forEach(function (m) {
        ((cfg.concerns[m] || {}).products || []).forEach(function (pslug) {
          if (cfg.bundles[pslug]) supersededBundles[pslug] = true;
        });
      });
      items.unshift(buildComboItem(combo, handle, 'existing', 'combo'));
    });
    // Rimuovi solo i BUNDLE singoli sostituiti da un combo (mai i combo tra loro).
    items = items.filter(function (it) {
      return !(it.kind === 'bundle' && !it.comboHandle && supersededBundles[it.slug]);
    });

    // 3) Dedup componenti (R18/R19 #3): un prodotto SINGOLO va rimosso solo se è
    //    già componente di un kit/combo presente (es. ProbioticPet singolo da
    //    "intestino" non si mostra se è già dentro un kit consigliato). Regola
    //    non aggressiva: si filtrano SOLO i prodotti singoli; i kit non si
    //    deduplicano MAI tra loro sul componente condiviso (opzione A), e nessun
    //    prodotto singolo non coperto da un kit viene perso (le problematiche non
    //    coinvolte nei kit — es. CalmPet per lo stress — restano).
    var inBundle = {};
    items.forEach(function (it) {
      if (it.kind !== 'bundle') return;
      self.kitComponents(it, profile).forEach(function (slug) { inBundle[slug] = true; });
    });
    items = items.filter(function (it) { return it.kind === 'bundle' || !inBundle[it.slug]; });

    return items;
  };

  // R21: trova la voce comboBundles il cui handle (per la specie) corrisponde.
  Quiz.prototype.findComboByHandle = function (handle, species) {
    if (!handle) return null;
    var list = this.config.comboBundles || [];
    for (var i = 0; i < list.length; i++) {
      var c = list[i];
      if (c.handleBySpecies && c.handleBySpecies[species] === handle) return c;
    }
    return null;
  };

  // Risolve handle e (per i prodotti) SKU/variante/packSize target
  Quiz.prototype.resolveTarget = function (item, profile) {
    var cfg = this.config;
    var species = profile.species;
    if (item.kind === 'bundle') {
      // R21 PARTE 1.3 (Kit Orecchie): un kit può avere una VARIANTE per fascia di
      // peso (variantByWeight). Selezioniamo SKU/variante corretti; pickVariant li
      // userà. La "quantità consigliata" (kitRecommendedQuantity) è indipendente.
      if (item.comboHandle) {
        var ct = { handle: item.comboHandle };
        var cvbw = item.comboVariantByWeight;
        if (cvbw && cvbw[species] && cvbw[species][profile.weight]) {
          var cv = cvbw[species][profile.weight];
          ct.sku = cv.sku; ct.variantId = cv.variantId || null;
          ct.tierVariant = true; // R24.B: variante-tier per fascia → aggiungi 1
        }
        return ct;
      }
      var b = cfg.bundles[item.slug];
      var bt = { handle: b.handleBySpecies ? b.handleBySpecies[species] : b.handle };
      if (b.variantByWeight && b.variantByWeight[species] && b.variantByWeight[species][profile.weight]) {
        var bv = b.variantByWeight[species][profile.weight];
        bt.sku = bv.sku; bt.variantId = bv.variantId || null;
        bt.tierVariant = true; // R24.B: variante-tier per fascia → aggiungi 1
      }
      return bt;
    }
    var p = cfg.products[item.slug];
    var handle = (p.handleBySpecies && p.handleBySpecies[species]) || p.handle;
    var target = { handle: handle, product: p };
    // variante per peso (ProbioticPet cane: 30 fino a 10 kg / Maxi 60 oltre)
    if (p.variantByWeight && p.variantByWeight[species] && p.variantByWeight[species][profile.weight]) {
      var v = p.variantByWeight[species][profile.weight];
      target.sku = v.sku;
      target.packSize = v.packSize;
      target.variantId = v.variantId || null; // R16 Parte B: fallback selezione variante
    } else {
      target.sku = p.sku ? p.sku[species] : null;
      target.packSize = (p.packSizeBySpecies && p.packSizeBySpecies[species]) || p.packSize || null;
    }
    return target;
  };

  /* ----------------------------------------------------------------------
     Shopify: prodotti live
     ---------------------------------------------------------------------- */
  Quiz.prototype.fetchProduct = function (handle) {
    if (!handle) return Promise.resolve(null);
    return fetch(this.rootUrl + '/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  };

  // Sceglie la variante da acquistare: match SKU se indicato, poi (R16 Parte B)
  // per id variante come fallback (ProbioticPet Cane/Maxi sulla stessa pagina),
  // altrimenti prima disponibile.
  function pickVariant(product, sku, variantId) {
    if (!product || !product.variants) return null;
    if (sku) {
      var bySku = product.variants.filter(function (v) { return String(v.sku) === String(sku); })[0];
      if (bySku) return bySku;
    }
    if (variantId) {
      var byId = product.variants.filter(function (v) { return String(v.id) === String(variantId); })[0];
      if (byId) return byId;
    }
    return product.variants.filter(function (v) { return v.available; })[0] || product.variants[0];
  }

  /* ----------------------------------------------------------------------
     Calcolo dose / durata / quantita (Fase 3 §7)
     ---------------------------------------------------------------------- */
  // Testo leggibile del ciclo consigliato (ogni quanto ripetere / durata).
  // R16 (Parte A.2): testi validati dalla responsabile scientifica, resi
  // verbatim. Alcune forme sono nuove ("X giorni, ogni Y mesi", "una tantum",
  // ciclo acuto esteso), altre arricchite (asNeeded con casi cronici, muta).
  function regimeLabel(cycle) {
    if (!cycle) return '';
    switch (cycle.type) {
      case 'days_periodic':
        return cycle.days + ' giorni' +
          (cycle.timesPerYear ? ', ' + cycle.timesPerYear + ' volte l\'anno' : '') +
          (cycle.emphasis ? ' (' + cycle.emphasis + ')' : '');
      // R16: "30 giorni, ogni 6 mesi" (ProbioticPet/DermaPet/OralProPet prevenzione)
      case 'days_every_months':
        return cycle.days + ' giorni, ogni ' + cycle.everyMonths + ' mesi';
      // R16: "60 giorni (una tantum, quando si presenta il problema)" (HairSkinPet problema)
      case 'days_once':
        return cycle.days + ' giorni (una tantum, quando si presenta il problema)';
      case 'days': return cycle.days + ' giorni';
      case 'pack_periodic': return cycle.everyMonths ? '1 confezione ogni ' + cycle.everyMonths + ' mesi' : '';
      // R16: ciclo acuto ProbioticPet Fast (verbatim)
      case 'acute':
        return 'Ciclo acuto: ' + cycle.daysMin + '–' + cycle.daysMax + ' giorni (minimo ' +
          cycle.daysMin + ', massimo ' + cycle.daysMax + ' consecutivi). Se il disturbo persiste, sentire il veterinario.';
      // R16: detergente in mantenimento
      case 'weekly': return 'Settimanale (per mantenere la pulizia)';
      // R16: "Al bisogno", con eventuale nota casi cronici (CalmPet da solo)
      case 'asNeeded':
        return 'Al bisogno' + (cycle.chronic ? ' (nei casi cronici: ' + regimeLabel(cycle.chronic) + ')' : '');
      default: return '';
    }
  }

  // Forme singolare/plurale delle unità (fix v5 #3). Chiave = valore in config.
  var UNIT_FORMS = {
    'cpr': ['compressa', 'compresse'],
    'compressa': ['compressa', 'compresse'],
    'bustine': ['bustina', 'bustine'],
    'bustina': ['bustina', 'bustine'],
    'soft chews': ['soft chew', 'soft chews'],
    'gtt': ['goccia', 'gocce'],
    'confezione': ['confezione', 'confezioni'],
    'ml': ['ml', 'ml']
  };
  function unitWord(n, unit) {
    var forms = UNIT_FORMS[unit];
    var singular = (n === 1 || n === 0.5); // ½ compressa (singolare), 2 compresse
    if (forms) return singular ? forms[0] : forms[1];
    return unit; // unità sconosciuta: invariata
  }
  // "1 compressa", "½ compressa", "2 bustine", "1 confezione"
  function qtyLabel(n, unit) {
    var num = (n === 0.5) ? '½' : n;
    return num + ' ' + unitWord(n, unit);
  }

  // Dose giornaliera di un prodotto per specie/peso (per singoli e componenti kit)
  function dailyDose(product, profile) {
    if (!product || !product.posology) return null;
    var pos = product.posology;
    var dose;
    // R16 (A.3): dose gatto può essere per fascia di peso (ProbioticPet Gatto)
    // oltre che valore unico.
    if (profile.species === 'gatto') dose = (pos.gatto && typeof pos.gatto === 'object') ? pos.gatto[profile.weight] : pos.gatto;
    else if (pos.cane && typeof pos.cane === 'object') dose = pos.cane[profile.weight];
    else dose = pos.cane;
    return typeof dose === 'number' ? dose : null;
  }
  function doseText(product, profile) {
    var d = dailyDose(product, profile);
    if (d == null) return 'al bisogno';
    return qtyLabel(d, product.packUnit || 'pz') + '/giorno';
  }

  // Testo con "Leggi di più" / "Mostra meno" (fix v4 #6)
  function renderExpandable(text) {
    var wrap = el('div', { class: 'lp-quiz__desc' });
    var p = el('p', { class: 'lp-quiz__desc-text is-clamped', text: text });
    wrap.appendChild(p);
    if (text.length > 180) {
      var btn = el('button', { type: 'button', class: 'lp-quiz__more', text: 'Leggi di più' });
      btn.addEventListener('click', function () {
        var clamped = p.classList.toggle('is-clamped');
        btn.textContent = clamped ? 'Leggi di più' : 'Mostra meno';
      });
      wrap.appendChild(btn);
    }
    return wrap;
  }

  // R16 (A.6): box "Come si usa" (modalità d'uso). Mostrato sotto le righe di
  // somministrazione. Testo lungo → paragrafo che va a capo (a differenza delle
  // righe dose che sono nowrap). Ritorna null se non c'è testo.
  function buildUsageBox(label, text) {
    if (!text) return null;
    return el('div', { class: 'lp-quiz__usage' }, [
      el('div', { class: 'lp-quiz__usage-label', text: label || 'Come si usa' }),
      el('p', { class: 'lp-quiz__usage-text', text: text })
    ]);
  }

  // Box con righe etichetta/valore (dosaggi o componenti kit)
  function buildDoseBox(lines, title) {
    var box = el('div', { class: 'lp-quiz__dose' });
    if (title) box.appendChild(el('div', { class: 'lp-quiz__dose-title', text: title }));
    lines.forEach(function (l) {
      box.appendChild(el('div', { class: 'lp-quiz__dose-line' }, [
        el('span', { class: 'lp-quiz__dose-label', text: l.label || l.name }),
        el('span', { class: 'lp-quiz__dose-value', text: l.value })
      ]));
    });
    return box;
  }

  Quiz.prototype.computeDose = function (item, target, profile) {
    var p = target.product;
    if (!p || !p.posology) return null;
    var pos = p.posology;
    var dose;
    // R16 (A.3): dose gatto può essere per fascia di peso (ProbioticPet Gatto)
    if (profile.species === 'gatto') dose = (pos.gatto && typeof pos.gatto === 'object') ? pos.gatto[profile.weight] : pos.gatto;
    else if (pos.cane && typeof pos.cane === 'object') dose = pos.cane[profile.weight];
    else dose = pos.cane;
    if (typeof dose !== 'number') return null; // prodotti "al bisogno"

    var packSize = target.packSize;
    var unit = p.packUnit || 'pz';
    var durationDays = packSize ? Math.floor(packSize / dose) : null;

    var regime = item.regime || 'existing';
    var cycle = (p.cycles && p.cycles[regime]) || null;
    var packs = null;
    var cycleText = '';

    if (cycle) {
      // Confezioni per ciclo: per i cicli espressi in giorni di somministrazione
      // (days_periodic / days_every_months / days_once / days) è ceil(giorni/durata
      // di una confezione). Per i cicli acuti / "al bisogno" non si stima.
      if (cycle.type === 'days_periodic' || cycle.type === 'days_every_months' ||
          cycle.type === 'days_once' || cycle.type === 'days') {
        packs = durationDays ? Math.ceil(cycle.days / durationDays) : null;
      } else if (cycle.type === 'pack_periodic') {
        packs = cycle.packs || 1;
      }
      // Il testo del ciclo mostrato è sempre quello validato (regimeLabel).
      cycleText = regimeLabel(cycle);
    }

    return {
      dose: dose, unit: unit, durationDays: durationDays, packs: packs,
      cycleText: cycleText, regimeText: regimeLabel(cycle), emphasis: cycle && cycle.emphasis
    };
  };

  // Box somministrazione — 3 voci in ordine, etichette da settings.dosageLabels
  // (fix v6 #5): Dosaggio giornaliero · Ciclo consigliato · Confezioni per ciclo.
  // La riga "Durata confezione" è stata rimossa.
  Quiz.prototype.doseLines = function (dose) {
    if (!dose) return [];
    var lab = (this.config.settings || {}).dosageLabels || {};
    var lines = [];
    lines.push({ label: lab.daily || 'Dosaggio giornaliero', value: qtyLabel(dose.dose, dose.unit) + ' al giorno' });
    if (dose.regimeText) lines.push({ label: lab.cycle || 'Ciclo consigliato', value: dose.regimeText });
    if (dose.packs) lines.push({ label: lab.packs || 'Confezioni per ciclo', value: qtyLabel(dose.packs, 'confezione') });
    return lines;
  };

  // R16 (Parte B): nome visualizzato del prodotto per specie/peso. ProbioticPet
  // ha nomi coerenti per formato — "ProbioticPet Cane" (fino a 10 kg),
  // "ProbioticPet Cane Maxi" (da 10 kg in su, variante per peso) e
  // "ProbioticPet Gatto" (nameBySpecies) — con fallback al nome base.
  Quiz.prototype.productDisplayName = function (slug, profile) {
    var p = this.config.products[slug];
    if (!p) return '';
    var sp = profile.species;
    var byW = p.variantByWeight && p.variantByWeight[sp] && p.variantByWeight[sp][profile.weight];
    if (byW && byW.name) return byW.name;
    if (p.nameBySpecies && p.nameBySpecies[sp]) return p.nameBySpecies[sp];
    return p.name;
  };

  // R20: formato FISSO di un componente DENTRO un kit/combo (oggi solo ProbioticPet
  // cane). I kit sono prodotti Shopify preconfezionati: ProbioticPet ha un formato
  // fisso indipendente dal peso del cane. Ritorna 'maxi' | 'standard' (default) per
  // ProbioticPet, null per gli altri componenti (nessun override → risoluzione
  // normale). Il Kit Movimento & Digestione dichiara 'maxi' via componentFormats;
  // tutti gli altri kit usano il default 'standard' (Cane 30 cpr).
  Quiz.prototype.kitComponentFormat = function (item, slug) {
    if (slug !== 'probioticpet') return null;
    var fmt = null;
    if (item && item.comboComponentFormats) fmt = item.comboComponentFormats[slug];
    else if (item) {
      var b = this.config.bundles[item.slug];
      if (b && b.componentFormats) fmt = b.componentFormats[slug];
    }
    return fmt || 'standard';
  };

  // R20: profilo "di formato" per risolvere NOME/SKU/confezione di ProbioticPet nel
  // kit al formato FISSO del kit (via kitFormatBands → chiave di variantByWeight.cane),
  // lasciando invariato il resto. La DOSE giornaliera resta calcolata sul peso REALE
  // (usando il profilo originale), quindi qui si sovrascrive solo `weight` per la
  // risoluzione del formato. Gatto e componenti non-ProbioticPet: profilo invariato.
  Quiz.prototype.kitComponentFormatProfile = function (item, slug, profile) {
    var fmt = this.kitComponentFormat(item, slug);
    if (!fmt || profile.species !== 'cane') return profile;
    var p = this.config.products[slug];
    var band = p && p.kitFormatBands && p.kitFormatBands[fmt];
    if (!band || !(p.variantByWeight && p.variantByWeight.cane && p.variantByWeight.cane[band])) return profile;
    return Object.assign({}, profile, { weight: band });
  };

  // R20: nome del componente ProbioticPet come mostrato dentro un kit (formato fisso)
  Quiz.prototype.kitComponentName = function (item, slug, profile) {
    return this.productDisplayName(slug, this.kitComponentFormatProfile(item, slug, profile));
  };

  // Righe dose dei componenti di un kit (bundle/combo), da componentsBySpecies
  Quiz.prototype.kitComponentLines = function (components, profile) {
    var self = this;
    var cfg = this.config;
    return (components || []).map(function (slug) {
      var p = cfg.products[slug];
      if (!p) return null;
      return { name: self.productDisplayName(slug, profile), value: doseText(p, profile) };
    }).filter(Boolean);
  };

  /* ----------------------------------------------------------------------
     Risultati
     ---------------------------------------------------------------------- */
  Quiz.prototype.renderResults = function () {
    var self = this;
    var cfg = this.config;
    var profile = this.buildProfile();
    this.profile = profile;
    this.root.classList.add('lp-quiz--results'); // contenitore più largo per i risultati

    // stato di caricamento
    this.mount.innerHTML = '';
    this.mount.appendChild(el('div', { class: 'lp-quiz__loading' }, [
      el('div', { class: 'lp-quiz__spinner' }),
      el('p', { text: 'Stiamo preparando il piano di ' + (profile.name || 'benessere') + '…' })
    ]));

    var candidates = this.buildCandidates(profile);
    var isProblemScenario = this.effectiveConcerns(profile).length > 0;

    // Candidati "di supporto" per raggiungere il minimo prodotti (fix v3 #11):
    // prodotti di prevenzione (priorityOrder) non già presenti, per la specie.
    // R18: esclude anche i prodotti che sono GIÀ componenti di un kit/combo
    // consigliato (es. ProbioticPet dentro il kit orecchie) — niente doppioni.
    var candidateSlugs = {};
    var candidateComponentSlugs = {};
    candidates.forEach(function (c) {
      candidateSlugs[c.slug] = true;
      if (c.kind === 'bundle') {
        self.kitComponents(c, profile).forEach(function (s) { candidateComponentSlugs[s] = true; });
      }
    });
    var priority = ((cfg.prevention || {}).priorityOrder) || [];
    var fillerItems = priority.filter(function (slug) {
      var p = cfg.products[slug];
      return p && (!p.species || p.species.indexOf(profile.species) !== -1) &&
        !candidateSlugs[slug] && !candidateComponentSlugs[slug];
    }).map(function (slug) {
      return { slug: slug, kind: 'product', regime: 'preventive', source: 'support', support: true };
    });

    // Cross-sell: Kit Cura solo se NON è stata scelta alcuna voce di igiene
    // quotidiana (Fase 1 §4). R21 PARTE 3.2: 'cura' non è più una problematica; la
    // scelta è ora in profile.cura (vuoto se l'utente ha risposto "No, nessuna").
    var crossSell = null;
    if (profile.cura.length === 0 && cfg.bundles.kit_cura) {
      var kh = cfg.bundles.kit_cura.handleBySpecies[profile.species];
      if (kh) crossSell = { handle: kh };
    }

    function fetchList(items) {
      return Promise.all(items.map(function (item) {
        var target = self.resolveTarget(item, profile);
        return self.fetchProduct(target.handle).then(function (product) {
          return { item: item, target: target, product: product };
        });
      }));
    }
    var crossFetch = crossSell ? this.fetchProduct(crossSell.handle) : Promise.resolve(null);

    Promise.all([fetchList(candidates), fetchList(fillerItems), crossFetch]).then(function (res) {
      var resolved = res[0];
      var fillers = res[1];
      var crossProduct = res[2];

      // Filtro "solo attivi e acquistabili"
      var hideOOS = (cfg.settings.outOfStock || {}).mode !== 'show';
      function available(r) {
        if (!r.product) return false;                 // non attivo / non pubblicato
        if (hideOOS && !r.product.available) return false; // esaurito
        return true;
      }
      resolved = resolved.filter(available);
      fillers = fillers.filter(available);

      // Tetto: 4 con problematiche (anche misto), 3 se sola prevenzione (fix v6 #7)
      var cap = isProblemScenario
        ? (cfg.settings.maxProducts || 4)
        : (cfg.settings.maxProductsPrevention || cfg.settings.maxProducts || 3);

      // R17.2: le problematiche selezionate dall'utente hanno priorità e NON
      // vengono mai escluse a favore della prevenzione. La prevenzione (regola
      // senior, "always", "di supporto") riempie solo lo spazio restante fino al
      // tetto; se il tetto costringe a tagliare, si taglia prima la prevenzione.
      var concernResolved = resolved.filter(function (r) { return !isPreventionItem(r.item); });
      var preventionResolved = resolved.filter(function (r) { return isPreventionItem(r.item); });
      var remaining = Math.max(0, cap - concernResolved.length);
      resolved = concernResolved.concat(preventionResolved.slice(0, remaining));

      // Minimo prodotti: se da problematica escono meno di minProducts,
      // completa con i "di supporto" (senza duplicati, restando nel tetto).
      var minProducts = cfg.settings.minProducts || 0;
      var target = Math.min(minProducts, cap);
      if (isProblemScenario && resolved.length < target) {
        for (var i = 0; i < fillers.length && resolved.length < target; i++) {
          var dup = resolved.some(function (r) { return r.item.slug === fillers[i].item.slug; });
          if (!dup) resolved.push(fillers[i]);
        }
      }

      self.recommended = resolved;
      self.renderResultsView(profile, resolved, crossProduct);

      // Klaviyo dopo consenso
      if (self.answers.consent) self.sendToKlaviyo(profile, resolved);

      // GA4 quiz_complete: stesso punto e stessa condizione di "Quiz Completato"
      // (riga sopra), senza attendere la risposta di Klaviyo. Flag azzerato in restart().
      if (self.answers.consent && !self._gaCompleted) {
        self._gaCompleted = true;
        gaTrack('quiz_complete', { quiz_species: profile.species });
      }
    });
  };

  Quiz.prototype.renderResultsView = function (profile, resolved, crossProduct) {
    var self = this;
    var cfg = this.config;
    var name = profile.name || 'il tuo amico';
    var wrap = el('div', { class: 'lp-quiz__results' });

    // R22 B.7: l'intestazione compatta "Il meglio per il tuo amico a 4 zampe?"
    // NON compare sulla pagina risultati (resta sulle altre schermate del quiz):
    // qui c'è già il titolo "Ecco il piano di …".

    // Intestazione + carta d'identità del pet
    var head = el('div', { class: 'lp-quiz__results-head' });
    head.appendChild(el('h2', { class: 'lp-quiz__results-title', text: 'Ecco il piano personale di ' + name + ' 🐾' }));
    wrap.appendChild(head);
    wrap.appendChild(this.renderIdentityCard(profile));

    if (!resolved.length) {
      wrap.appendChild(el('p', { class: 'lp-quiz__error', text: 'Al momento non troviamo prodotti disponibili per questo profilo. Riprova più tardi o scrivici.' }));
    }

    // Box intro prevenzione: SOLO nei risultati 100% prevenzione (nessuna
    // problematica selezionata). Nei risultati misti non compare (fix v6 #9).
    var isPurePrevention = this.effectiveConcerns(profile).length === 0;
    var prevIntro = (cfg.prevention || {}).intro;
    if (isPurePrevention && prevIntro) {
      wrap.appendChild(el('p', { class: 'lp-quiz__prevention-intro', text: fill(prevIntro, name) }));
    }

    // R26.B: "Aggiungi tutti al carrello" in cima alla lista. Aggiunge in un'unica
    // chiamata tutti i prodotti/kit consigliati, con variante e quantità identiche
    // ai pulsanti singoli (cartLineFor). Mostrato solo se c'è almeno una riga valida.
    var allLines = resolved.map(function (r) { return self.cartLineFor(r, profile); }).filter(Boolean);
    if (allLines.length) {
      var addAllBtn = el('button', {
        type: 'button', class: 'lp-quiz__btn lp-quiz__btn--primary lp-quiz__addall',
        text: 'Aggiungi tutti al carrello'
      });
      addAllBtn.addEventListener('click', function () { self.addAllToCart(addAllBtn, allLines); });
      wrap.appendChild(el('div', { class: 'lp-quiz__addall-wrap' }, [addAllBtn]));
    }

    // Griglia prodotti: affiancati su desktop, incolonnati su mobile
    var grid = el('div', { class: 'lp-quiz__products' });
    resolved.forEach(function (r) {
      grid.appendChild(self.renderProductCard(r, profile));
    });
    wrap.appendChild(grid);

    // Cross-sell "Completa la routine"
    var showCross = crossProduct && (crossProduct.available || (self.config.settings.outOfStock || {}).mode === 'show');
    if (showCross) {
      wrap.appendChild(self.renderCrossSell(crossProduct, profile));
    }

    // Consulenza (stagionale)
    var cons = cfg.settings.consultation || {};
    if (cons.enabled && cons.url) {
      wrap.appendChild(el('div', { class: 'lp-quiz__note' }, [
        el('span', { text: 'Hai dubbi? ' }),
        el('a', { href: cons.url, text: cons.label || 'Consulenza veterinaria gratuita' })
      ]));
    }

    // Conferma "piano inviato": box evidente e ben visibile (R9.3)
    if (profile.email) {
      var sent = el('div', { class: 'lp-quiz__sent' });
      sent.appendChild(el('span', {
        class: 'lp-quiz__sent-icon', 'aria-hidden': 'true',
        html: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<path d="M20 7L9.5 17.5 4 12" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      }));
      sent.appendChild(el('div', { class: 'lp-quiz__sent-body' }, [
        el('p', { class: 'lp-quiz__sent-title', text: 'Ti abbiamo inviato il piano' }),
        el('p', { class: 'lp-quiz__sent-text', text:
          'Abbiamo inviato il piano di ' + name + ' a ' + profile.email + '.' })
      ]));
      wrap.appendChild(sent);

      // R22 B.9: nota sconto SOTTO il blocco verde, con più evidenza (riquadro
      // tenue, non un banner promozionale). Mostrata solo se c'è un testo
      // configurato (settings.incentive.note). inc.enabled resta false in config.
      var inc = cfg.settings.incentive || {};
      if (inc.note) {
        wrap.appendChild(el('p', { class: 'lp-quiz__incentive-note', text: fill(inc.note, name) }));
      }
    }

    // Ripeti il test: elemento secondario/defilato (R9.3)
    wrap.appendChild(el('div', { class: 'lp-quiz__restart' }, [
      el('button', {
        type: 'button', class: 'lp-quiz__btn lp-quiz__btn--ghost lp-quiz__restart-link',
        text: 'Ripeti il test',
        onclick: function () { self.restart(); }
      })
    ]));

    this.mount.innerHTML = '';
    this.mount.appendChild(wrap);
  };

  // Riavvia il quiz da capo
  Quiz.prototype.restart = function () {
    this.answers = { concerns: [], cura: [] };
    this.recommended = null;
    this.profile = null;
    this._gaStarted = false;   // nuovo percorso: nuovo quiz_start e quiz_complete
    this._gaCompleted = false;
    this._completionId = null;  // nuovo percorso: nuovo evento "Quiz Completato"
    this._klaviyoSentId = null;
    this.root.classList.remove('lp-quiz--results');
    this.currentToken = 'species';
    this.renderStep();
    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // R27: etichetta età come mostrata nella carta d'identità dei risultati, riusata
  // nell'evento Klaviyo (pet_age_label). Data nota → "N anni"; fascia scelta →
  // etichetta italiana completa dell'opzione age_band; '—' se età assente.
  Quiz.prototype.petAgeLabel = function (profile) {
    if (profile.ageYears != null) return profile.ageYears + (profile.ageYears === 1 ? ' anno' : ' anni');
    if (profile.ageBandValue) {
      var abq = this.q('age_band');
      var opts = (abq && abq.optionsBySpecies && abq.optionsBySpecies[profile.species]) || [];
      var match = opts.filter(function (o) { return o.value === profile.ageBandValue; })[0];
      if (match) return match.label;
    }
    return '—';
  };

  // Carta d'identità del pet in cima ai risultati (settings.identityCard)
  Quiz.prototype.renderIdentityCard = function (profile) {
    var self = this;
    var idc = (this.config.settings || {}).identityCard || {};
    if (idc.enabled === false) return el('div', {});
    var fields = idc.fields || ['name', 'species', 'age', 'weight_band', 'breed'];

    var weightLabels = {
      '<10': 'Fino a 10 kg', '10-25': '10–25 kg', '25-40': '25–40 kg', '>40': 'Oltre 40 kg',
      // R16 (A.1): nuove fasce di peso gatto
      '<4': 'Fino a 4 kg', '4-8': '4–8 kg', '>8': 'Oltre 8 kg'
    };
    // Età: data nota → "N anni"; fascia scelta → etichetta completa italiana
    // dall'opzione age_band (mai termini interni/inglesi). Fix v6 #6.
    function ageText() { return self.petAgeLabel(profile); }
    var values = {
      name: profile.name || '—',
      species: profile.species === 'gatto' ? 'Gatto' : 'Cane',
      age: ageText(),
      weight_band: weightLabels[profile.weight] || profile.weight || '—',
      breed: profile.breed || '—'
    };
    var labels = { name: 'Nome', species: 'Specie', age: 'Età', weight_band: 'Taglia', breed: 'Razza' };
    var speciesIcon = (idc.speciesIcon || {})[profile.species];

    var rows = el('div', { class: 'lp-quiz__id-fields' });
    fields.forEach(function (f) {
      var valueEl = el('span', { class: 'lp-quiz__id-value' });
      // R12: rimossa la piccola icona dalla riga "Specie" (solo testo Cane/Gatto)
      valueEl.appendChild(document.createTextNode(values[f] != null ? String(values[f]) : '—'));
      rows.appendChild(el('div', { class: 'lp-quiz__id-row' }, [
        el('span', { class: 'lp-quiz__id-label', text: labels[f] || f }),
        valueEl
      ]));
    });

    var children = [];
    // R22 B.6: icona specie DENTRO la card, a sinistra dei testi, sullo stesso
    // sfondo bianco (niente sovrapposizione che la taglia). Disco/alone tenue
    // dietro l'icona. Se il file manca (onerror) iconEl rimuove l'img: in tal caso
    // l'iconwrap resta vuoto e viene nascosto via CSS (:empty). Risolve anche il
    // problema mobile (B.5): l'icona è un elemento interno, sempre visibile.
    var heroIcon = speciesIcon ? self.iconEl(speciesIcon, 'lp-quiz__id-icon') : null;
    if (heroIcon) {
      children.push(el('div', { class: 'lp-quiz__id-iconwrap', 'aria-hidden': 'true' }, [heroIcon]));
    }
    // Slot foto: predisposto ma disattivato di default (nessun caricamento ora)
    if (idc.showPhoto) {
      children.push(el('div', { class: 'lp-quiz__id-photo', 'aria-hidden': 'true' }));
    }
    children.push(el('div', { class: 'lp-quiz__id-body' }, [
      rows
    ]));

    return el('div', {
      class: 'lp-quiz__id-card' + (idc.showPhoto ? ' lp-quiz__id-card--with-photo' : '') + (heroIcon ? ' lp-quiz__id-card--with-icon' : '')
    }, children);
  };

  // Testo esteso del prodotto/bundle, per specie e per origine (§ modifiche v2)
  Quiz.prototype.cardDescription = function (r, profile) {
    var cfg = this.config, sp = profile.species, item = r.item;
    if (item.kind === 'bundle') {
      if (item.comboDesc) return item.comboDesc[sp] || '';
      var b = cfg.bundles[item.slug];
      return b && b.descriptionBySpecies ? (b.descriptionBySpecies[sp] || '') : '';
    }
    var p = cfg.products[item.slug];
    if (!p) return '';
    if (item.regime === 'preventive' && p.preventionBlurbBySpecies && p.preventionBlurbBySpecies[sp]) {
      return p.preventionBlurbBySpecies[sp];
    }
    return p.descriptionBySpecies ? (p.descriptionBySpecies[sp] || '') : '';
  };

  // R18 (1.3): titolo mostrato per un PRODOTTO SINGOLO. Per i prodotti con nome
  // per variante-di-peso o per specie (ProbioticPet: "Cane" / "Cane Maxi" da 10 kg
  // in su / "Gatto") il titolo della pagina Shopify non basta: la pagina Cane ha
  // un titolo unico anche quando la variante acquistata è la Maxi. Si usa quindi
  // il nome risolto per specie/peso (variantByWeight.name → nameBySpecies), con
  // fallback al titolo Shopify per tutti gli altri prodotti. I kit/combo
  // mantengono il titolo della pagina del kit (productDisplayName resta per i
  // componenti dentro la fisarmonica).
  Quiz.prototype.cardTitle = function (r, profile) {
    if (r.item.kind === 'product') {
      var p = this.config.products[r.item.slug];
      if (p) {
        var sp = profile.species;
        var byW = p.variantByWeight && p.variantByWeight[sp] && p.variantByWeight[sp][profile.weight];
        if (byW && byW.name) return byW.name;
        if (p.nameBySpecies && p.nameBySpecies[sp]) return p.nameBySpecies[sp];
      }
    }
    return r.product.title;
  };

  // Componenti di un kit (bundle/combo) da componentsBySpecies
  Quiz.prototype.kitComponents = function (item, profile) {
    var sp = profile.species;
    if (item.comboComponents) return item.comboComponents[sp] || [];
    var b = this.config.bundles[item.slug];
    return (b && b.componentsBySpecies) ? (b.componentsBySpecies[sp] || []) : [];
  };

  // Tag delle problematiche che hanno fatto scattare il consiglio (R9.2).
  // Testi da settings.concernLabels, max settings.maxConcernTags, dedup per
  // etichetta. Solo le problematiche selezionate dall'utente per questo item:
  // prevenzione/senior NON generano tag (restano i badge); i "di supporto"
  // hanno solo il badge (nessuna problematica selezionata li ha fatti scattare).
  Quiz.prototype.concernTagsFor = function (item, profile) {
    var cfg = this.config;
    var labels = (cfg.settings || {}).concernLabels || {};
    var max = (cfg.settings || {}).maxConcernTags || 3;
    if (!item || item.support || item.source === 'prevenzione' || item.source === 'senior') return [];

    var keys;
    if (item.comboMatches && item.comboMatches.length) {
      keys = item.comboMatches.slice();
    } else if (item.source === 'cura') {
      // Kit Cura: le sotto-voci scelte (occhi/orecchie/zampe) → un'unica etichetta
      // "Cura e igiene quotidiana" con la key 'cura', così R18 (3.3) le assegna
      // l'icona icon-cura.png (le sotto-voci cura_* non sono in concernIcons).
      var curaLab = (profile.cura || []).map(function (c) { return labels[c]; }).filter(Boolean)[0];
      return curaLab ? [{ key: 'cura', label: curaLab }] : [];
    } else {
      // Problematiche selezionate la cui mappatura prodotto include questo item
      keys = this.effectiveConcerns(profile).filter(function (c) {
        return ((cfg.concerns[c] || {}).products || []).indexOf(item.slug) !== -1;
      });
    }

    var seen = {};
    var out = [];
    keys.forEach(function (k) {
      var lab = labels[k];
      if (!lab || seen[lab]) return;
      seen[lab] = true;
      // R23.3: le sotto-voci igiene (cura_occhi/cura_orecchie/cura_zampe) non sono
      // in concernIcons (sono nella domanda 'cura', non in 'concerns'), quindi il
      // pill non trovava l'icona. Normalizziamo qualsiasi chiave 'cura*' a 'cura',
      // che è mappata a icon-cura.png (fallback nel costruttore) → icona a video.
      var iconKey = (String(k).indexOf('cura') === 0) ? 'cura' : k;
      out.push({ key: iconKey, label: lab });
    });
    return out.slice(0, max);
  };

  // Le 3 righe di dosaggio (settings.dosageLabels) per un componente di un kit.
  // R18 (2.2): `cycleOverride` (stringa) sostituisce il testo della riga "Ciclo
  // consigliato" SOLO per quel componente in quel kit (es. Detergente Auricolare
  // dentro il kit orecchie → "1 volta a settimana per la durata del ciclo di
  // ProbioticPet", invece di "Al bisogno").
  Quiz.prototype.componentDoseLines = function (item, slug, regime, profile, cycleOverride) {
    var p = this.config.products[slug];
    if (!p) return [];
    var cLab = ((this.config.settings || {}).dosageLabels || {}).cycle || 'Ciclo consigliato';
    // R20: nome/SKU/confezione dal formato FISSO del kit (fmtProfile), ma la DOSE
    // giornaliera resta calcolata sul peso REALE (profile). Confezioni per ciclo:
    // ricalcolate sul packSize del formato reale del kit (rifinitura rimandata).
    var fmtProfile = this.kitComponentFormatProfile(item, slug, profile);
    var target = this.resolveTarget({ slug: slug, kind: 'product' }, fmtProfile);
    var dose = this.computeDose({ slug: slug, kind: 'product', regime: regime || 'existing' }, target, profile);
    var lines = this.doseLines(dose);
    if (cycleOverride) {
      // sostituisci (o aggiungi) la riga "Ciclo consigliato" con l'override del kit
      var found = false;
      lines.forEach(function (l) { if (l.label === cLab) { l.value = cycleOverride; found = true; } });
      if (!found) lines.push({ label: cLab, value: cycleOverride });
      return lines;
    }
    if (!lines.length) {
      // Prodotti "al bisogno": mostra almeno il ciclo consigliato
      var reg = p.cycles ? regimeLabel(p.cycles[regime] || p.cycles.existing) : '';
      if (reg) lines = [{ label: cLab, value: reg }];
    }
    return lines;
  };

  // R21 PARTE 1: quantità consigliata (numero di kit/confezioni del bundle) per
  // la fascia di peso. Combo → comboRecommendedQuantity; bundle → recommendedQuantity
  // in config. Il valore per specie può essere un numero fisso (es. Kit Orecchie: 1)
  // o una mappa per fascia di peso (cane). Ritorna null se il kit non ha quantità
  // definita (es. Kit Cura) → comportamento invariato.
  Quiz.prototype.kitRecommendedQuantity = function (item, profile) {
    var rq = null;
    if (item.comboHandle) rq = item.comboRecommendedQuantity;
    else if (item.kind === 'bundle') {
      var b = this.config.bundles[item.slug];
      rq = b && b.recommendedQuantity;
    }
    if (!rq) return null;
    var v = rq[profile.species];
    if (v == null) return null;
    if (typeof v === 'number') return v;
    return v[profile.weight] != null ? v[profile.weight] : null;
  };

  // R22 A.3: periodo di copertura (giorni) mostrato nel testo della quantità kit.
  // Default 30; un kit può dichiarare coverageDaysByBand per fascia di peso (solo
  // il Kit Movimento & Digestione a ≤10 e 10–25 kg → 60, ciclo intero).
  Quiz.prototype.kitCoverageDays = function (item, profile) {
    var map = null;
    if (item.comboHandle) map = item.comboCoverageDaysByBand;
    else if (item.kind === 'bundle') {
      var b = this.config.bundles[item.slug];
      map = b && b.coverageDaysByBand;
    }
    var def = ((this.config.settings || {}).kitCoverageDaysDefault) || 30;
    if (map && map[profile.weight] != null) return map[profile.weight];
    return def;
  };

  // R21 PARTE 1 / R23.2: due righe volute — principale "Quantità consigliata: {qty}
  // kit" (in evidenza) + secondaria più piccola "copre circa {N} giorni" ({N} =
  // kitCoverageDays: 30, o 60 per Movimento & Digestione ≤25 kg). Nota sul ciclo
  // invariata sotto. Sostituisce "Confezioni per ciclo" a livello di kit.
  Quiz.prototype.renderKitQuantity = function (item, profile, qty) {
    var settings = this.config.settings || {};
    var lab = (settings.dosageLabels || {}).quantity || 'Quantità consigliata';
    var note = settings.kitCycleNote;
    var days = this.kitCoverageDays(item, profile);
    var box = el('div', { class: 'lp-quiz__dose lp-quiz__kit-qty' });
    box.appendChild(el('p', { class: 'lp-quiz__kit-qty-text' }, [
      document.createTextNode(lab + ': '),
      el('strong', { class: 'lp-quiz__kit-qty-num', text: qty + ' kit' })
    ]));
    box.appendChild(el('p', { class: 'lp-quiz__kit-qty-cover', text: 'copre circa ' + days + ' giorni' }));
    if (note) box.appendChild(el('p', { class: 'lp-quiz__kit-qty-note', text: note }));
    return box;
  };

  // R22 B.2: righe di somministrazione di un componente di kit, come mostrate
  // nell'accordion e nell'evento Klaviyo. Nei kit "a quantità" (hasQty) resta la
  // sola dose giornaliera; se il componente NON ha dose giornaliera (detergente,
  // prodotti "al bisogno") si mostra una riga "Frequenza" (override del kit, es.
  // Detergente nel Kit Orecchie, oppure il ciclo del prodotto) così la box non
  // resta vuota. Fuori dai kit a quantità restano le righe complete.
  Quiz.prototype.kitComponentDisplayLines = function (item, slug, regime, profile, ov, hasQty) {
    var full = this.componentDoseLines(item, slug, regime, profile, ov);
    if (!hasQty) return full;
    var lab = (this.config.settings || {}).dosageLabels || {};
    var dailyLab = lab.daily || 'Dosaggio giornaliero';
    var freqLab = lab.frequency || 'Frequenza';
    var daily = full.filter(function (l) { return l.label === dailyLab; });
    if (daily.length) return daily;
    // Nessuna dose giornaliera → riga frequenza (override del kit o ciclo prodotto)
    var p = this.config.products[slug];
    var freqVal = ov || (p && p.cycles ? regimeLabel(p.cycles[regime] || p.cycles.existing) : '');
    return freqVal ? [{ label: freqLab, value: freqVal }] : [];
  };

  // Fisarmonica "Dettagli somministrazione del kit" (R9.5): chiusa di default,
  // per ogni componente nome + righe di dosaggio (via kitComponentDisplayLines).
  Quiz.prototype.renderKitDosage = function (item, components, profile) {
    var self = this;
    var cfg = this.config;
    var regime = item.regime;
    var cycleOverrides = item.comboComponentCycles;
    var hasQty = this.kitRecommendedQuantity(item, profile) != null;
    var details = el('details', { class: 'lp-quiz__kit-details' });
    details.appendChild(el('summary', { class: 'lp-quiz__kit-summary' }, [
      el('span', { text: 'Dettagli somministrazione del kit' })
    ]));
    var usageLab = ((cfg.settings || {}).dosageLabels || {}).usage || 'Come si usa';
    var body = el('div', { class: 'lp-quiz__kit-details-body' });
    components.forEach(function (slug) {
      var p = cfg.products[slug];
      if (!p) return;
      var ov = cycleOverrides && cycleOverrides[slug];
      // R20: nome del componente = formato FISSO del kit (kitComponentName)
      var lines = self.kitComponentDisplayLines(item, slug, regime, profile, ov, hasQty);
      body.appendChild(buildDoseBox(lines, self.kitComponentName(item, slug, profile)));
      // R16 (A.6): modalità d'uso del componente sotto le sue righe dose
      var usageBox = buildUsageBox(usageLab, p.usage);
      if (usageBox) body.appendChild(usageBox);
    });
    details.appendChild(body);
    return details;
  };

  // R23.5: dettagli somministrazione di un PRODOTTO SINGOLO nella stessa tendina
  // collassabile dei kit (stesso markup/classi lp-quiz__kit-details, chiusa di
  // default come i kit). Contenuto invariato: dosaggio giornaliero, ciclo,
  // confezioni per ciclo (o fallback ciclo "al bisogno") + "Come si usa".
  Quiz.prototype.renderProductDosage = function (r, profile) {
    var cfg = this.config;
    var details = el('details', { class: 'lp-quiz__kit-details' });
    details.appendChild(el('summary', { class: 'lp-quiz__kit-summary' }, [
      el('span', { text: 'Dettagli somministrazione' })
    ]));
    var body = el('div', { class: 'lp-quiz__kit-details-body' });
    var dose = this.computeDose(r.item, r.target, profile);
    var lines = this.doseLines(dose);
    if (!lines.length) {
      // prodotti "al bisogno": mostra almeno il ciclo consigliato
      var p = cfg.products[r.item.slug];
      var reg = p && p.cycles ? regimeLabel(p.cycles[r.item.regime] || p.cycles.existing) : '';
      var cLab = ((cfg.settings || {}).dosageLabels || {}).cycle || 'Ciclo consigliato';
      if (reg) lines = [{ label: cLab, value: reg }];
    }
    if (lines.length) body.appendChild(buildDoseBox(lines));
    // R16 (A.6): modalità d'uso sotto le righe di somministrazione
    var usageProd = cfg.products[r.item.slug];
    var usageLab = ((cfg.settings || {}).dosageLabels || {}).usage || 'Come si usa';
    var usageBox = usageProd ? buildUsageBox(usageLab, usageProd.usage) : null;
    if (usageBox) body.appendChild(usageBox);
    details.appendChild(body);
    return details;
  };

  // Card prodotto/kit a righe: media (sinistra) · testo (centro) · dosaggi+CTA (destra)
  Quiz.prototype.renderProductCard = function (r, profile) {
    var self = this;
    var product = r.product;
    var card = el('div', { class: 'lp-quiz__card' });

    // Media
    var img = product.featured_image || (product.images && product.images[0]);
    var media = el('div', { class: 'lp-quiz__card-media' });
    if (img) media.appendChild(el('img', { class: 'lp-quiz__card-img', src: img, alt: product.title, loading: 'lazy' }));
    card.appendChild(media);

    // Testo centrale
    var main = el('div', { class: 'lp-quiz__card-main' });

    // R10.2: riga superiore — icona a sinistra, poi (alla sua destra) badge e
    // tag della/e problematica/he, tutti sulla stessa linea. Poi il nome, poi
    // la descrizione. Regole tag invariate da R9.2 (concernTagsFor).
    var headRow = el('div', { class: 'lp-quiz__card-head' });
    var blockIcon = this.iconEl(this.iconForItem(r.item), 'lp-quiz__block-icon');
    if (blockIcon) headRow.appendChild(blockIcon);
    // R18 (3.1): etichetta unica "Prevenzione" per TUTTI gli extra (prevenzione
    // e "di supporto" per il minimo prodotti): niente più distinzione "Di supporto".
    if (r.item.support || r.item.regime === 'preventive') {
      headRow.appendChild(el('span', { class: 'lp-quiz__card-badge', text: 'Prevenzione' }));
    }
    // Tag delle problematiche che hanno fatto scattare il consiglio (R9.2)
    var tags = this.concernTagsFor(r.item, profile);
    tags.forEach(function (t) {
      var pill = el('span', { class: 'lp-quiz__card-tag' });
      var ic = self.iconEl(self.concernIcons[t.key], 'lp-quiz__card-tag-icon');
      if (ic) pill.appendChild(ic);
      pill.appendChild(el('span', { text: t.label }));
      headRow.appendChild(pill);
    });
    if (headRow.childNodes.length) main.appendChild(headRow);

    main.appendChild(el('p', { class: 'lp-quiz__card-title', text: this.cardTitle(r, profile) }));
    var desc = this.cardDescription(r, profile);
    if (desc) main.appendChild(renderExpandable(fill(desc, profile.name)));
    card.appendChild(main);

    // Colonna destra: dosaggi + pulsante
    var aside = el('div', { class: 'lp-quiz__card-aside' });

    if (r.item.kind === 'product') {
      // R23.5: prodotto singolo nella stessa tendina collassabile dei kit
      // (dose, ciclo, confezioni, come si usa — contenuto invariato).
      aside.appendChild(this.renderProductDosage(r, profile));
    } else {
      // KIT: R21 PARTE 1 — "Quantità consigliata: N" + nota ciclo, poi fisarmonica
      // con i dosaggi (dose giornaliera + modalità d'uso per componente).
      var kitQty = this.kitRecommendedQuantity(r.item, profile);
      if (kitQty != null) aside.appendChild(this.renderKitQuantity(r.item, profile, kitQty));
      var comps = this.kitComponents(r.item, profile);
      if (comps.length) aside.appendChild(this.renderKitDosage(r.item, comps, profile));
    }

    var variant = pickVariant(product, r.target.sku, r.target.variantId);
    var addQty = this.addQtyFor(r, profile);
    var btn = el('button', {
      type: 'button', class: 'lp-quiz__btn lp-quiz__btn--primary lp-quiz__btn--full',
      text: 'Aggiungi al carrello',
      disabled: variant ? false : 'disabled',
      onclick: function () { if (variant) self.addToCart(variant.id, btn, addQty); }
    });
    aside.appendChild(btn);
    card.appendChild(aside);

    return card;
  };

  Quiz.prototype.renderCrossSell = function (product, profile) {
    var self = this;
    var name = profile.name || 'il tuo amico';
    var variant = pickVariant(product, null);
    var kitDesc = ((this.config.bundles.kit_cura || {}).descriptionBySpecies || {})[profile.species] || '';
    var img = product.featured_image || (product.images && product.images[0]);

    var box = el('div', { class: 'lp-quiz__crosssell' });
    box.appendChild(el('p', { class: 'lp-quiz__crosssell-title', text: 'Completa la routine di ' + name }));
    box.appendChild(el('p', { html: 'Tutto il necessario per la routine di ' + escapeHtml(name) + ', in un <span class="lp-highlight">unico kit</span>.' }));

    var media = el('div', { class: 'lp-quiz__crosssell-media' });
    if (img) media.appendChild(el('img', { class: 'lp-quiz__crosssell-img', src: img, alt: product.title, loading: 'lazy' }));
    var info = el('div', { class: 'lp-quiz__crosssell-info' }, [
      el('p', { class: 'lp-quiz__card-title', text: product.title }),
      kitDesc ? el('p', { class: 'lp-quiz__crosssell-desc', text: fill(kitDesc, name) }) : null
    ]);
    media.appendChild(info);
    box.appendChild(media);

    var btn = el('button', {
      type: 'button', class: 'lp-quiz__btn lp-quiz__btn--primary',
      text: 'Aggiungi il kit',
      disabled: variant ? false : 'disabled',
      onclick: function () { if (variant) self.addToCart(variant.id, btn); }
    });
    box.appendChild(btn);
    return box;
  };

  // R26.A: apre la pagina carrello in una NUOVA SCHEDA (approccio app-agnostico:
  // niente <a href="/cart"> che gli interceptor delle app cart-drawer catturano).
  Quiz.prototype.openCart = function () {
    window.open(this.rootUrl + '/cart', '_blank');
  };

  // R24.B/R26.B: quantità da aggiungere al carrello per un item. 1 se è una
  // variante-tier per fascia (la variante codifica già la quantità) o un singolo;
  // altrimenti (kit senza tier per la fascia, es. Prurito da Stress cane
  // 10–25/25–40/>40) = quantità consigliata (variante base × N). Unica fonte
  // condivisa tra card singola e "Aggiungi tutti", così il risultato è identico.
  Quiz.prototype.addQtyFor = function (r, profile) {
    if (r.item.kind === 'bundle' && !r.target.tierVariant) {
      return this.kitRecommendedQuantity(r.item, profile) || 1;
    }
    return 1;
  };

  // R26.B: riga carrello {id, quantity} per un item risolto, con la STESSA variante
  // (pickVariant su sku/variantId del target) e quantità dell'aggiunta singola.
  // Ritorna null se il prodotto non è disponibile / senza variante valida.
  Quiz.prototype.cartLineFor = function (r, profile) {
    if (!r || !r.product) return null;
    var variant = pickVariant(r.product, r.target.sku, r.target.variantId);
    if (!variant) return null;
    return { id: variant.id, quantity: this.addQtyFor(r, profile) };
  };

  // R26.B: "Aggiungi tutti al carrello" — un'unica chiamata /cart/add.js con più
  // items (variante+quantità di ciascuno identiche all'aggiunta singola). Controlla
  // l'esito (422 → "Riprova", niente falso "aggiunti"); su successo conferma,
  // aggiorna il bollino e apre /cart in una nuova scheda. Guardia: se non c'è nulla
  // da aggiungere, no-op.
  Quiz.prototype.addAllToCart = function (btn, lines) {
    var self = this;
    if (!lines || !lines.length) return; // no-op
    var original = btn.textContent;
    btn.disabled = true; btn.textContent = 'Aggiungo…'; btn.classList.remove('is-added');
    fetch(this.rootUrl + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: lines })
    }).then(function (r) {
      return r.json().then(function (data) { return { ok: r.ok, status: r.status, data: data }; });
    }).then(function (res) {
      if (!res.ok) {
        var msg = (res.data && (res.data.description || res.data.message)) || ('HTTP ' + res.status);
        console.warn('[lp-quiz] "Aggiungi tutti" /cart/add.js non riuscito:', res.status, msg);
        btn.textContent = 'Riprova';
        btn.disabled = false;
        return;
      }
      btn.textContent = 'Aggiunti al carrello ✓';
      btn.classList.add('is-added');
      self.updateCartBadge();
      self.openCart(); // /cart in nuova scheda; i risultati restano aperti
      setTimeout(function () {
        btn.textContent = original;
        btn.classList.remove('is-added');
        btn.disabled = false;
      }, 2500);
    }).catch(function (e) {
      console.warn('[lp-quiz] "Aggiungi tutti" errore di rete:', e);
      btn.textContent = 'Riprova'; btn.disabled = false;
    });
  };

  Quiz.prototype.addToCart = function (variantId, btn, quantity) {
    var self = this;
    var qty = quantity && quantity > 0 ? quantity : 1; // R21 PARTE 1: quantità kit
    var original = btn.textContent;
    btn.disabled = true; btn.textContent = 'Aggiungo…'; btn.classList.remove('is-added');
    // R24.A: controlla l'esito REALE della chiamata (response.ok / status 422),
    // leggendo sempre il corpo JSON (Shopify vi mette description/message anche
    // in errore). Niente più falso "Aggiunto" quando l'add fallisce.
    fetch(this.rootUrl + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
    }).then(function (r) {
      return r.json().then(function (data) { return { ok: r.ok, status: r.status, data: data }; });
    }).then(function (res) {
      if (!res.ok) {
        var msg = (res.data && (res.data.description || res.data.message)) || ('HTTP ' + res.status);
        console.warn('[lp-quiz] /cart/add.js non riuscito:', res.status, msg);
        btn.textContent = 'Riprova';
        btn.disabled = false;
        return;
      }
      // R25: nessun drawer (su questa pagina non è caricato). Conferma esplicita:
      // il pulsante diventa "Aggiunto al carrello ✓" e torna cliccabile dopo qualche
      // secondo; compare un link "Vai al carrello" (→ /cart). Nessun reload/redirect.
      btn.textContent = 'Aggiunto al carrello ✓';
      btn.classList.add('is-added');
      self.revealGoToCart(btn);
      self.updateCartBadge();
      setTimeout(function () {
        btn.textContent = original;
        btn.classList.remove('is-added');
        btn.disabled = false;
      }, 2500);
    }).catch(function (e) {
      console.warn('[lp-quiz] /cart/add.js errore di rete:', e);
      btn.textContent = 'Riprova'; btn.disabled = false;
    });
  };

  // R25 / R25.1: mostra (creandolo la prima volta) "Vai al carrello" accanto al
  // pulsante di aggiunta. NON è un <a href="/cart">: le app cart-drawer
  // (ReConvert / Essential Slide Cart / Monster) agganciano i click su
  // a[href*="/cart"] e aprebbero un loro drawer (vuoto) invece di navigare. Usiamo
  // un <a> SENZA href verso /cart + navigazione "dura" via JS (window.top, con
  // fallback a window), così nessun selettore degli interceptor combacia e la
  // pagina /cart si apre davvero. Aspetto invariato (stessa classe .lp-quiz__gocart).
  Quiz.prototype.revealGoToCart = function (btn) {
    var self = this;
    var container = btn.parentNode;
    if (!container) return;
    var link = container.querySelector('.lp-quiz__gocart');
    if (!link) {
      // R26.A: apre /cart in una NUOVA SCHEDA (window.open _blank), così la pagina
      // risultati resta aperta. Chiamato da un click diretto → non bloccato.
      var go = function () { self.openCart(); };
      link = el('a', {
        class: 'lp-quiz__gocart',
        role: 'button',
        tabindex: '0',
        text: 'Vai al carrello',
        onclick: go
      });
      link.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); go(); }
      });
      container.appendChild(link);
    }
    link.classList.remove('is-hidden');
  };

  // R25: aggiorna il bollino/totale del carrello in testata (se presenti a pagina)
  // usando /cart.js — nessuna dipendenza dal drawer. Guardia: se gli elementi non
  // esistono, no-op silenzioso; errori non bloccanti.
  Quiz.prototype.updateCartBadge = function () {
    var countEls = document.querySelectorAll('[data-header-cart-count]');
    var totalEls = document.querySelectorAll('[data-header-cart-total]');
    if (!countEls.length && !totalEls.length) return; // no-op: niente da aggiornare
    fetch(this.rootUrl + '/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        countEls.forEach(function (e) { e.textContent = cart.item_count; });
        // Totale: formattato con Shopify.money_format se disponibile (altrimenti si
        // lascia invariato — il contatore è comunque il segnale principale).
        if (totalEls.length && window.Shopify && Shopify.money_format) {
          var t = formatMoney(cart.total_price, Shopify.money_format);
          totalEls.forEach(function (e) { e.textContent = t; });
        }
      })
      .catch(function () { /* non bloccante */ });
  };

  /* ----------------------------------------------------------------------
     Klaviyo (Fase 3 §8) — solo dopo consenso
     ---------------------------------------------------------------------- */

  // R15: righe di somministrazione di un PRODOTTO SINGOLO, identiche a quelle
  // mostrate dalla card risultati (renderProductCard): stessa computeDose sullo
  // stesso r.item/r.target, stesse doseLines e stesso fallback "al bisogno".
  Quiz.prototype.cardProductDoseLines = function (r, profile) {
    var dose = this.computeDose(r.item, r.target, profile);
    var lines = this.doseLines(dose);
    if (!lines.length) {
      // prodotti "al bisogno": mostra almeno il ciclo consigliato (come la card)
      var p = this.config.products[r.item.slug];
      var reg = p && p.cycles ? regimeLabel(p.cycles[r.item.regime] || p.cycles.existing) : '';
      var cLab = ((this.config.settings || {}).dosageLabels || {}).cycle || 'Ciclo consigliato';
      if (reg) lines = [{ label: cLab, value: reg }];
    }
    return lines;
  };

  // R15: componenti di un KIT con le stesse righe della fisarmonica
  // "Dettagli somministrazione del kit" (renderKitDosage): stessi componenti
  // (kitComponents) e stesse righe per componente (componentDoseLines).
  Quiz.prototype.cardKitComponents = function (r, profile) {
    var self = this;
    var overrides = r.item.comboComponentCycles;
    // R21 PARTE 1 / R22 B.2: righe componente identiche all'accordion dei risultati
    // (kitComponentDisplayLines): nei kit a quantità solo dose giornaliera oppure,
    // se assente, la riga "Frequenza"; modalità d'uso invariata.
    var hasQty = this.kitRecommendedQuantity(r.item, profile) != null;
    return this.kitComponents(r.item, profile).map(function (slug) {
      var p = self.config.products[slug];
      if (!p) return null;
      // R20: nome + confezione dal formato FISSO del kit, coerente con la card
      var lines = self.kitComponentDisplayLines(r.item, slug, r.item.regime, profile, overrides && overrides[slug], hasQty);
      var comp = { name: self.kitComponentName(r.item, slug, profile), dosage_lines: lines };
      // R16 (A.6): modalità d'uso del componente, come mostrata nell'accordion
      if (p.usage) comp.usage = p.usage;
      return comp;
    }).filter(Boolean);
  };

  Quiz.prototype.buildKlaviyoPayload = function (profile, resolved) {
    var self = this;
    var skus = [];
    var snapshots = [];
    resolved.forEach(function (r) {
      var v = pickVariant(r.product, r.target.sku, r.target.variantId);
      var sku = v && v.sku ? String(v.sku) : '';
      if (sku) skus.push(sku);
      // Snapshot = productSnapshotFields (title,url,image,price) + sku (R14.2)
      var snap = {
        title: self.cardTitle(r, profile),
        url: r.product.url,
        image: r.product.featured_image || (r.product.images && r.product.images[0]) || '',
        price: r.product.price,
        sku: sku
      };
      // R27: ID numerico della variante usata per l'aggiunta al carrello (la stessa
      // di addToCart: tier per fascia / Orecchie / default singoli / ProbioticPet
      // 30-Maxi per peso). Numero puro, non GID.
      if (v && v.id != null) snap.variant_id = v.id;
      // R15: descrizione per la specie corrente, identica a quella mostrata nei
      // risultati (cardDescription + fill del nome). Omessa se vuota.
      var desc = self.cardDescription(r, profile);
      if (desc) snap.description = fill(desc, profile.name);
      // R15: somministrazione, coerente con la card risultati.
      if (r.item.kind === 'bundle') {
        // Kit/combo: components[] (dosage_lines a livello prodotto resta assente)
        var comps = self.cardKitComponents(r, profile);
        if (comps.length) snap.components = comps;
        // R21 PARTE 1: quantità consigliata del kit (per fascia di peso). Assente
        // sui prodotti singoli. Kit Orecchie = 1 (la variante contiene la quantità).
        var kitQty = self.kitRecommendedQuantity(r.item, profile);
        if (kitQty != null) {
          snap.recommended_quantity = kitQty;
          // R28: copertura in giorni mostrata nei risultati ("copre circa N giorni"),
          // stessa logica del render (kitCoverageDays): 30 default, 60 per Kit
          // Movimento & Digestione a ≤10 e 10–25 kg. Solo sui KIT con quantità.
          snap.coverage_days = self.kitCoverageDays(r.item, profile);
        }
      } else {
        // Prodotto singolo: dosage_lines[] (o le righe "al bisogno")
        var lines = self.cardProductDoseLines(r, profile);
        if (lines.length) snap.dosage_lines = lines;
        // R16 (A.6): modalità d'uso, come mostrata nei risultati. Omessa se vuota.
        var prod = self.config.products[r.item.slug];
        if (prod && prod.usage) snap.usage = prod.usage;
      }
      snapshots.push(snap);
    });
    var concerns = profile.concerns.concat(profile.cura).filter(function (v, i, a) { return a.indexOf(v) === i; });
    var bd = profile.birthdate;
    var birthday = (bd && !bd.unknown && bd.year)
      ? bd.year + '-' + String(bd.month || 1).padStart(2, '0') + '-' + String(bd.day || 1).padStart(2, '0')
      : null;

    var props = {
      pet_name: profile.name,
      pet_species: profile.species,
      pet_breed: profile.breed,
      pet_weight_band: profile.weight,
      pet_diet: profile.diet,
      pet_concerns: concerns,
      pet_supplement_history: profile.supplementHistory,
      quiz_source: profile.source,
      quiz_recommended_skus: skus,
      quiz_completed_at: new Date().toISOString().slice(0, 10)
    };
    if (birthday) props.pet_birthday = birthday;
    else if (profile.ageBandValue) props.pet_age_band = bandLabel(profile.ageBandValue);

    // R27: etichetta età leggibile (come la carta d'identità), a livello evento.
    var ageLabel = self.petAgeLabel(profile);
    if (ageLabel && ageLabel !== '—') props.pet_age_label = ageLabel;

    // R27: cart-permalink che aggiunge tutti i prodotti consigliati e porta al
    // carrello (stesse variante+quantità dell'opzione C "Aggiungi tutti"). Formato
    // /cart/{variant_id}:{q},...  Dominio primario dello store.
    var cartLines = resolved.map(function (r) { return self.cartLineFor(r, profile); }).filter(Boolean);
    if (cartLines.length) {
      props.cart_all_url = 'https://longevity-pet.com/cart/' +
        cartLines.map(function (l) { return l.id + ':' + l.quantity; }).join(',') +
        '?storefront=true'; // R29: atterra sulla pagina carrello (non checkout)
    }

    // Facoltativi: inviati solo se compilati (fix v3 #17)
    if (profile.sex) props.pet_sex = profile.sex;
    if (profile.neutered) props.pet_neutered = profile.neutered;

    return { props: props, snapshots: snapshots };
  };

  Quiz.prototype.sendToKlaviyo = function (profile, resolved) {
    var cfg = this.config.klaviyo || {};
    if (cfg.consentRequired && !this.answers.consent) return;
    var payload = this.buildKlaviyoPayload(profile, resolved);
    var eventName = cfg.eventName || 'Quiz Completato';

    // Un id per ogni completamento: va nell'evento (quiz_completion_id) e come
    // unique_id della Client Events API, così Klaviyo scarta i doppioni anche
    // lato server. Azzerato in restart(): chi rifà il quiz genera un nuovo evento.
    if (!this._completionId) {
      this._completionId = (window.crypto && typeof window.crypto.randomUUID === 'function')
        ? window.crypto.randomUUID()
        : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }

    // 1) Onsite Klaviyo (klaviyo.js gia caricato sullo store): solo identify.
    //    Il track onsite è stato tolto: duplicava l'evento della Client Events
    //    API (punto 3) e il flusso partiva due volte.
    try {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['identify', Object.assign({ $email: profile.email }, payload.props)]);
      } else if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['identify', Object.assign({ $email: profile.email }, payload.props)]);
      }
    } catch (e) { /* non blocca i risultati */ }

    // Le chiamate Client API richiedono la chiave pubblica e il consenso.
    if (!cfg.publicApiKey || !this.answers.consent) return;
    // Anti-doppio: iscrizioni ed evento una sola volta per completamento.
    if (this._klaviyoSentId === this._completionId) return;
    this._klaviyoSentId = this._completionId;
    var completionId = this._completionId;
    var company = encodeURIComponent(cfg.publicApiKey);
    var revision = '2024-10-15'; // stessa revision delle chiamate già presenti

    // 2) Iscrizione via Client API a ENTRAMBE le liste (R14.1): listId (S2Zyzu)
    //    e listIdEmailSubscribers (RWzCgK), stesso consenso e stesse proprietà.
    //    Un errore di rete su una lista non blocca l'altra né i risultati.
    function subscribeToList(listId) {
      if (!listId) return;
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + company, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': revision },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email: profile.email, properties: payload.props } } }
            },
            relationships: { list: { data: { type: 'list', id: listId } } }
          }
        })
      }).catch(function (e) { console.warn('[lp-quiz] Klaviyo subscribe fallita (list ' + listId + ')', e); });
    }
    subscribeToList(cfg.listId);
    subscribeToList(cfg.listIdEmailSubscribers);

    // 3) Evento metrica "Quiz Completato" via Client Events API (R14.2):
    //    proprietà pet + (se abilitato) prodotti consigliati con snapshot + sku.
    var eventProps = Object.assign({}, payload.props, { quiz_completion_id: completionId });
    if (cfg.eventIncludesRecommendedProducts) {
      eventProps.recommended_products = payload.snapshots;
    }
    fetch('https://a.klaviyo.com/client/events/?company_id=' + company, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': revision },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            properties: eventProps,
            unique_id: completionId,
            metric: { data: { type: 'metric', attributes: { name: eventName } } },
            profile: { data: { type: 'profile', attributes: { email: profile.email } } }
          }
        }
      })
    }).catch(function (e) { console.warn('[lp-quiz] Klaviyo evento "' + eventName + '" fallito', e); });
  };

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */
  function boot(root) {
    if (root._lpQuizBooted) return;
    root._lpQuizBooted = true;
    var configUrl = root.getAttribute('data-config-url');
    var rootUrl = root.getAttribute('data-root-url') || '/';

    var mount = root.querySelector('.lp-quiz__mount') || root;
    mount.innerHTML = '<div class="lp-quiz__loading"><div class="lp-quiz__spinner"></div><p>Carico il quiz…</p></div>';

    fetch(configUrl).then(function (r) { return r.json(); }).then(function (config) {
      // Override dall'editor tema
      var settingsNode = document.querySelector('[data-lp-quiz-settings]');
      if (settingsNode) {
        try {
          var over = JSON.parse(settingsNode.textContent);
          if (over.moultMonthsCsv) {
            over.moultMonths = over.moultMonthsCsv.split(',').map(function (n) { return parseInt(n.trim(), 10); }).filter(function (n) { return !isNaN(n); });
            delete over.moultMonthsCsv;
          }
          if (!over.maxProducts) delete over.maxProducts;
          config.settings = deepMerge(config.settings, over);
        } catch (e) { /* usa config as-is */ }
      }
      var quiz = new Quiz(root, config, rootUrl, configUrl);
      quiz.start();
    }).catch(function () {
      mount.innerHTML = '<div class="lp-quiz__error">Non riesco a caricare il quiz. Ricarica la pagina.</div>';
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-lp-quiz]');
    Array.prototype.forEach.call(roots, boot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
